import axios from "axios";
import moment from "moment";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Endpoints that run before a session exists (login, sign-up, forgot/reset
// password). A stale/expired token left over in localStorage from a previous
// session must never be attached here — some backends validate the Bearer
// token on every route, so a leftover bad token makes a fresh login itself
// fail with 400 until localStorage is cleared by hand.
const PUBLIC_AUTH_ENDPOINTS = [
  "/auth/login",
  "/auth/sign-up",
  "/auth/recovery-password",
  "/auth/set-new-password",
];

const isPublicAuthEndpoint = (url = "") =>
  PUBLIC_AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("fms-maintenance-token");
    if (token && !isPublicAuthEndpoint(config.url)) {
      config.headers.authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("fms-maintenance-token");
      localStorage.removeItem("fms-maintenance-details");

      window.location.href = "/";
    }
    return Promise.reject(error);
  },
);

export const apiService = {
  login: async (credentials) => {
    try {
      const response = await api.post("/auth/login", credentials);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  register: async (credentials) => {
    try {
      const response = await api.post("/auth/sign-up", credentials);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  uploadCsv: async (formData) => {
    try {
      const response = await api.post("/imports/csv", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  proceedCsv: async (formData) => {
    try {
      const response = await api.post("/imports/csv-proceed", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getMaintenanceData: async (params) => {
    try {
      const response = await api.post(
        `/maintenance/paginated?page=${params.page}&limit=${params.limit}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getTyreData: async (params) => {
    try {
      const url = `/tyres/paginated?page=${params.page}&limit=${params.limit}`;
      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyres"
      );
    }
  },

  getUsersRole: async () => {
    try {
      const response = await api.get(`/auth/user-roles-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createUser: async (userData) => {
    try {
      const response = await api.post("/auth/create-user", userData);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteUser: async (id) => {
    try {
      const response = await api.delete(`/auth/user-delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deletePermission: async (id) => {
    try {
      const response = await api.delete(
        `/app-permissions/permission-group/${id}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteService: async (id) => {
    try {
      const response = await api.delete(`/maintenance-service/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getSingleService: async (id) => {
    try {
      const response = await api.get(`/maintenance-service/single/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getServiceOverview: async () => {
    try {
      const response = await api.get(`/maintenance-service/overview`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getSingleUser: async (id) => {
    try {
      const response = await api.get(`/auth/user-singe/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getUserDropdown: async (id) => {
    try {
      const response = await api.get(`/auth/users-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getDriverDropdown: async () => {
    try {
      const response = await api.get(`/auth/driver-dropdown`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver dropdown"
      );
    }
  },

  getHelperDropdown: async () => {
    try {
      const response = await api.get(`/auth/helper-dropdown`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch helper dropdown"
      );
    }
  },

  editUser: async (userData) => {
    try {
      const response = await api.post("/auth/edit-user", userData);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPaginatedUsers: async (params) => {
    try {
      const url = `/auth/users-paginated?page=${params.page}&limit=${params.limit}`;

      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPermissionApi: async (params) => {
    try {
      const url = `/auth/permission-paginated?page=${params.page}&limit=${params.limit}`;

      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updatePettyCash: async (data) => {
    try {
      const response = await api.patch("/petty-cash/update", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deletePettyCash: async (id) => {
    try {
      const response = await api.delete(`/petty-cash/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateTripExpense: async (data) => {
    try {
      const response = await api.patch("/trip-expenses/update", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteTripExpense: async (id) => {
    try {
      const response = await api.delete(`/trip-expenses/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPermissionDropdown: async () => {
    try {
      const response = await api.get(`/auth/permission-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getAllPermissionsDropdown: async () => {
    try {
      const response = await api.get(
        `/app-permissions/permissions-dropdown-badges`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getFetchModules: async () => {
    try {
      const response = await api.get(`/auth/fetch-modules`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createPermissionGroup: async (permissionData) => {
    try {
      const response = await api.post(
        "/auth/create-permission-group",
        permissionData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getSinglePermissionGroup: async (id) => {
    try {
      const response = await api.get(
        `/app-permissions/single-permission-group/${id}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updatePermissionGroup: async (permissionData) => {
    try {
      const response = await api.patch(
        "/auth/update-permission-group",
        permissionData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  forgotPassword: async (email) => {
    try {
      const response = await api.post("/auth/recovery-password", {
        email: email,
      });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  resetPassword: async (passwordData) => {
    try {
      const response = await api.post("/auth/set-new-password", {
        password: passwordData.password,
        confirm_password: passwordData.confirm_password,
        token: passwordData.token,
      });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  changePassword: async (passwordData) => {
    try {
      const response = await api.post("/auth/change-password", {
        password: passwordData.password,
        confirm_password: passwordData.confirm_password,
      });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  resetDefaultPassword: async (userId) => {
    try {
      const response = await api.post("/auth/default-password", {
        user_id: userId,
      });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVehiclesDropdown: async () => {
    try {
      const response = await api.get(`/vehicles/dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVehicleDetail: async (vehicleId, vehicleBrandId, ref_id) => {
    try {
      const response = await api.get(
        `/vehicles/detail?vehicle_id=${vehicleId}&vehicle_brand_id=${vehicleBrandId}&ref_id=${ref_id}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createServiceIssue: async (issueData) => {
    try {
      const response = await api.post("/service-issue/create", issueData);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateServiceIssue: async (issueData) => {
    try {
      const response = await api.patch("/service-issue/update", issueData);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createServiceEntry: async (serviceData) => {
    try {
      const response = await api.post(
        "/maintenance-service/create",
        serviceData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateServiceEntry: async (serviceData) => {
    try {
      const response = await api.patch(
        "/maintenance-service/patch",
        serviceData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getNextServiceId: async () => {
    try {
      const response = await api.get("/maintenance-service/next-id");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  uploadFiles: async (formData) => {
    try {
      const response = await api.post("/files-upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getReasonsDropdown: async () => {
    try {
      const response = await api.get(`/maintenance/reasons-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getCategoriesDropdown: async () => {
    try {
      const response = await api.get(`/maintenance/categories-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartsList: async (params = {}) => {
    try {
      const normalizedVehicleTypeId = Number(params?.vehicleTypeId);
      const normalizedVehicleId = Number(params?.vehicleId);
      const normalizedAssetId = Number(params?.assetId);
      const normalizedAssetType = String(params?.assetType || "").trim().toLowerCase();
      const queryParams = new URLSearchParams();

      if (
        Number.isFinite(normalizedVehicleTypeId) &&
        normalizedVehicleTypeId > 0
      ) {
        queryParams.set("vehicle_type_id", String(normalizedVehicleTypeId));
      }

      if (
        Number.isFinite(normalizedVehicleId) &&
        normalizedVehicleId > 0
      ) {
        queryParams.set("vehicle_id", String(normalizedVehicleId));
      }

      if (normalizedAssetType) {
        queryParams.set("asset_type", normalizedAssetType);
      }

      if (
        Number.isFinite(normalizedAssetId) &&
        normalizedAssetId > 0
      ) {
        queryParams.set("asset_id", String(normalizedAssetId));
      }

      const url = queryParams.toString()
        ? `/maintenance/parts-list?${queryParams.toString()}`
        : "/parts/parts-list";
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createServiceLineItem: async (lineItemData) => {
    try {
      const response = await api.post(
        "/service-line-items/create",
        lineItemData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateServiceLineItem: async (lineItemData) => {
    try {
      const response = await api.patch(
        "/service-line-items/update",
        lineItemData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getServicesPaginated: async (params) => {
    try {
      const url = `/maintenance-service/paginated?page=${params.page}&limit=${params.limit}`;

      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getServicesReminderPaginated: async (params) => {
    try {
      const url = `/maintenance-service/upcoming-paginated?page=${params.page}&limit=${params.limit}&vehicle_id=${params.vehicle_id}`;
      const response = await api.post(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  servicePdfExport: async (getIds) => {
    try {
      const response = await api.post("/reports/service-pdf", getIds);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  serviceExcelExport: async (getIds) => {
    try {
      const response = await api.post("/reports/service-excel", getIds);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteServiceLineItem: async (id) => {
    try {
      const response = await api.delete(`/service-line-items/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateServiceStatus: async (statusData) => {
    try {
      const response = await api.patch(
        "/maintenance-service/change-status",
        statusData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  changeStopOrders: async (data) => {
    try {
      const response = await api.patch("/trips/change-stop-orders", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ============== ISSUES ===================

  getIssuesOverview: async () => {
    try {
      const response = await api.get(`/service-issue/overview`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getIssuesPaginated: async (params) => {
    try {
      const url = `/service-issue/paginated?page=${params.page}&limit=${params.limit}`;

      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getUserIssuesPaginated: async (params) => {
    try {
      const url = `/service-issue/view-all-paginated?assign_to=${params.userId}&page=${params.page}&limit=${params.limit}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteIssue: async (id) => {
    try {
      const response = await api.delete(`/service-issue/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getIssueById: async (id) => {
    try {
      const response = await api.get(`/service-issue/detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  issuePdfExport: async (getIds) => {
    try {
      const response = await api.post("/reports/issue-service-pdf", getIds);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  issueExcelExport: async (getIds) => {
    try {
      const response = await api.post("/reports/issue-excel", getIds);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  issueLastId: async () => {
    try {
      const response = await api.get(`/service-issue/last-id`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getServiceEntryDropdown: async () => {
    try {
      const response = await api.get(`/maintenance-service/dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getServiceEntryByVehicleDropdown: async (params) => {
    try {
      const queryParams = new URLSearchParams();

      if (
        params !== null &&
        params !== undefined &&
        typeof params !== "object"
      ) {
        queryParams.set("vehicle_id", String(params));
      } else {
        const normalizedAssetType = String(params?.assetType || params?.asset_type || "")
          .trim()
          .toLowerCase();
        const normalizedAssetId =
          params?.assetId ??
          params?.asset_id ??
          params?.vehicle_id ??
          params?.equipment_id ??
          params?.tyre_id;

        if (normalizedAssetId !== "" && normalizedAssetId !== null && normalizedAssetId !== undefined) {
          if (normalizedAssetType === "equipment" || params?.equipment_id) {
            queryParams.set("equipment_id", String(normalizedAssetId));
          } else if (normalizedAssetType === "tyre" || params?.tyre_id) {
            queryParams.set("tyre_id", String(normalizedAssetId));
          } else {
            queryParams.set("vehicle_id", String(normalizedAssetId));
          }
        }
      }

      const response = await api.get(
        `/maintenance-service/dropdown?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createLinkIssue: async (linkIssueData) => {
    try {
      const response = await api.patch(
        "/service-issue/link-issue",
        linkIssueData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updatePriority: async (priorityData) => {
    try {
      const response = await api.patch(
        "/service-issue/change-priority",
        priorityData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateServicePriority: async (priorityData) => {
    try {
      const response = await api.patch(
        "/maintenance-service/change-priority",
        priorityData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateStatus: async (statusData) => {
    try {
      const response = await api.patch(
        "/service-issue/change-status",
        statusData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateTags: async (tagsData) => {
    try {
      const response = await api.post("/service-issue/add-tags", tagsData);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateAssignee: async (assigneeData) => {
    try {
      const response = await api.patch(
        "/service-issue/change-assignee",
        assigneeData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getIssueHistory: async (params) => {
    try {
      const url = `/service-issue/issue-history?history_type=${params.history_type}&service_issue_id=${params.service_issue_id}`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  commentOnIssue: async (data) => {
    try {
      const response = await api.post("/service-issue/comment-on-issue", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ============== WORK ORDER ===================

  getWorkOrderOverview: async () => {
    try {
      const response = await api.get(`/work-orders/overview`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getWorkOrderPaginated: async (params) => {
    try {
      const url = `/work-orders/paginated?page=${params.page}&limit=${params.limit}`;

      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVendorsDropdown: async () => {
    try {
      const response = await api.get(`/maintenance/vendors-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createWorkOrder: async (data) => {
    try {
      const response = await api.post("/work-orders/create", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getWorkOrderListingPdf: async (data = {}) => {
    try {
      const response = await api.post('/work-orders/export-pdf', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to generate work order listing PDF'
      );
    }
  },

  getWorkOrderListingExcel: async (data = {}) => {
    try {
      const response = await api.post('/work-orders/export-excel', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to generate work order listing Excel'
      );
    }
  },

  workOrderPdfExport: async (getIds) => {
    try {
      const response = await api.post("/reports/work-order-pdf", getIds);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  workOrderExcelExport: async (getIds) => {
    try {
      const response = await api.post("/reports/work-order-excel", getIds);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteWorkOrder: async (id) => {
    try {
      const response = await api.delete(`/work-orders/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getWorkOrderHistory: async (params) => {
    try {
      const url = `/work-orders/work-order-history?history_type=${params.history_type}&work_order_id=${params.work_order_id}`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  commentOnWorkOrder: async (data) => {
    try {
      const response = await api.post(
        "/work-orders/comment-on-work-order",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getWorkOrderById: async (id) => {
    try {
      const response = await api.get(`/work-orders/detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateWorkOrder: async (workOrderData) => {
    try {
      const response = await api.patch("/work-orders/update", workOrderData);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateRepairPriority: async (priorityData) => {
    try {
      const response = await api.patch(
        "/work-orders/update-repair-class",
        priorityData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateWorkOrderVendor: async (vendorData) => {
    try {
      const response = await api.patch(
        "/work-orders/update-vendor",
        vendorData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  workOrderLinkServiceEntry: async (linkServiceData) => {
    try {
      const response = await api.patch(
        "/work-orders/link-services",
        linkServiceData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  emailReminder: async (emailReminderData) => {
    try {
      const response = await api.post(
        "/work-orders/email-reminder",
        emailReminderData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  financialApprovalStatus: async (financialApprovalData) => {
    try {
      const response = await api.patch(
        "/work-orders/change-approval-financial-status",
        financialApprovalData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  operationalApprovalStatus: async (operationApprovalData) => {
    try {
      const response = await api.patch(
        "/work-orders/change-approval-operation-status",
        operationApprovalData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateWorkOrderAssignee: async (assigneeData) => {
    try {
      const response = await api.patch(
        "/work-orders/change-assignee",
        assigneeData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ============== TAGS ===================

  getServiceTagsDropdown: async () => {
    try {
      const response = await api.get(`/maintenance-service/tags-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getIssueTagsDropdown: async () => {
    try {
      const response = await api.get(`/service-issue/tags-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getIssueVehiclesDropdown: async () => {
    try {
      const response = await api.get(`/app-dropdown/all-vehicles-dropdown`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch issue vehicles dropdown"
      );
    }
  },

  getIssueEquipmentDropdown: async () => {
    try {
      const response = await api.get(`/app-dropdown/all-equipment-dropdown`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch issue equipment dropdown"
      );
    }
  },

  getIssueTyresDropdown: async () => {
    try {
      const response = await api.get(`/app-dropdown/all-tyres-dropdown`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch issue tyres dropdown"
      );
    }
  },

  getWorkOrderTagsDropdown: async () => {
    try {
      const response = await api.get(`/work-orders/tags-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateWorkOrderStatus: async (statusData) => {
    try {
      const response = await api.patch(
        "/work-orders/change-status",
        statusData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  invoiceWorkOrderPdf: async (statusData) => {
    try {
      const response = await api.post(
        "/reports/invoice-work-order-pdf",
        statusData,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ============== NOTIFICATIONS ===================

  getNotifications: async (params) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 15;
      const response = await api.get(`/notifications/paginated?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  seenAllNotifications: async () => {
    try {
      const response = await api.post("/notifications/seen-all", {});
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ==================== DROPDOWNS ====================

  getCustomerDropdown: async () => {
    try {
      const response = await api.get(`/trip-customer/dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },
  getCostCenterDropdown: async () => {
    try {
      const response = await api.get(`/cost-centers/dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },
  getVehiclesDropdown: async () => {
    try {
      const response = await api.get(`/vehicles/dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },
  getVehiclesDropdownWithEquipment: async () => {
    try {
      const response = await api.get(`/vehicles/dropdown-with-equipment`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message;
    }
  },
  getVehiclesTrackingPlusDropdown: async () => {
    try {
      const response = await api.get(`/vehicles/dropdown-trucking-plus`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },
  getVehiclesDropdownByVehicleType: async (vehicleTypeId) => {
    try {
      const safeId = encodeURIComponent(String(vehicleTypeId));
      const response = await api.get(`/vehicles/dropdown-by-vehicle-type/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicles by vehicle type"
      );
    }
  },
  getDropdownVehicleByCustomer: async (params = {}) => {
    try {
      const response = await api.get('/vehicles/dropdown-vehicle-by-customer', {
        params: {
          contract_id: params.contract_id,
          vehicle_type_id: params.vehicle_type_id,
          start_at: params.start_at,
          expected_end_at: params.expected_end_at,
        },
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicles by customer"
      );
    }
  },
  getVehiclesDropdownByCustomer: async (params = {}) => {
    try {
      const response = await api.get('/vehicles/dropdown-vehicle-by-customer', {
        params: {
          contract_id: params.contract_id,
          vehicle_type_id: params.vehicle_type_id,
          start_at: params.start_at,
          expected_end_at: params.expected_end_at,
        },
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicles by customer"
      );
    }
  },
  getUsersDropdown: async () => {
    try {
      const response = await api.get(`/auth/users-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },
  getSupervisorsDropdown: async () => {
    try {
      const response = await api.get(`/auth/users-dropdown-by-role?role_id=7`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch supervisors dropdown"
      );
    }
  },
  getBanksDropdown: async () => {
    try {
      const response = await api.get(`/banks/dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },
  getProductsDropdown: async () => {
    try {
      const response = await api.get(`/products/dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },
  getProductRatesByDate: async (payload) => {
    try {
      const response = await api.post(`/products/rates-by-date`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch product rates"
      );
    }
  },

  // ==================== TRIPS ====================

  createTrip: async (data) => {
    try {
      const response = await api.post(`/trips/create`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createTripStage: async (data) => {
    try {
      const response = await api.post(`/trip-stages/create`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create trip stage"
      );
    }
  },

  updateTripStops: async (data) => {
    try {
      const response = await api.post(`/trip-stages/update-trip-stops`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update trip stops"
      );
    }
  },

  getTripStopsByTripId: async (tripId) => {
    try {
      const response = await api.get(`/trip-stops/trip/${tripId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip stops"
      );
    }
  },

  updateTripStopsQuantity: async (data) => {
    try {
      const response = await api.patch(`/trip-stops/update-quantity`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update trip stops quantity"
      );
    }
  },

  getDriverDocChecklistDropdown: async () => {
    try {
      const response = await api.get(`/trip-stages/driver-doc-checklist-dropdown`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver document checklist"
      );
    }
  },

  getVehicleInspectionChecklistDropdown: async () => {
    try {
      const response = await api.get(`/trip-stages/vehicle-inspection-checklist-dropdown`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle inspection checklist"
      );
    }
  },

  updateChecklistsAndAllocations: async (data) => {
    try {
      const response = await api.post(`/trip-stages/update-checklists-and-allocations`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update checklists and allocations"
      );
    }
  },

  returnTripToCorrection: async (data) => {
    try {
      const response = await api.patch(`/trip-stages/return-to-correction`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to return trip for correction"
      );
    }
  },

  verifyTripStage: async (data) => {
    try {
      const response = await api.patch(`/trip-stages/verify`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to verify trip"
      );
    }
  },

  getTripListingPaginated: async (data = {}, page = 1, limit = 10) => {
    try {
      const response = await api.post(
        `/trip-stages/trip-listing-paginated?page=${page}&limit=${limit}`,
        data
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip listing"
      );
    }
  },

  getTripListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/trips/trip-listing-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate trip listing PDF"
      );
    }
  },

  getTripListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/trips/trip-listing-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate trip listing Excel"
      );
    }
  },

  overviewTrip: async () => {
    try {
      const response = await api.get(`/trips/overview`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  tripLists: async (data) => {
    try {
      const { page: requestedPage, limit: requestedLimit, ...payload } = data || {};
      const page = Number(requestedPage) > 0 ? Number(requestedPage) : 1;
      const limit = Number(requestedLimit) > 0 ? Number(requestedLimit) : 10;
      const response = await api.post(
        `/trips/${payload.activeTab === "live" ? "active-trips" : "all-trips-history"}?page=${page}&limit=${limit}`,
        payload,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  tripListingPaginated: async (params, payload) => {
    try {
      const { page = 1, limit = 10 } = params || {};
      const response = await api.post(
        `/trips/trip-listing-paginated?page=${page}&limit=${limit}`,
        payload || {}
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message || "Failed to fetch trip listing";
    }
  },

  viewAllTrips: async () => {
    try {
      const response = await api.get(`/trips/view-all`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message || "Failed to fetch trips";
    }
  },

  getTripDetails: async (id) => {
    try {
      const response = await api.get(`/trips/detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getTripLogs: async (id) => {
    try {
      const response = await api.get(`/trips/trip-logs/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateTrip: async ({ data }) => {
    try {
      const response = await api.patch(`/trips/update`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getTripTimeline: async (id) => {
    try {
      const response = await api.get(`/trips/trip-timeline/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] || "Failed to fetch trip timeline"
      );
    }
  },

  generateTripId: async () => {
    try {
      const response = await api.get(`/trips/generate-trip-id`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getTripHistory: async (params) => {
    try {
      const url = `/trips/trip-history?history_type=${params.history_type}&trip_id=${params.trip_id}`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  commentOnTrip: async (data) => {
    try {
      const response = await api.post("/trips/comment-on-strip", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  changeTripStatus: async (data) => {
    try {
      const response = await api.patch("/trips/status", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  tripStart: async (data) => {
    try {
      const response = await api.patch("/trips/trip-start", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to start trip"
      );
    }
  },

  tripClosed: async (data) => {
    try {
      const response = await api.patch("/trips/trip-closed", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  actualTripComplete: async (data) => {
    try {
      const response = await api.patch("/trips/actual-trip-complete", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteTrip: async (id, reason) => {
    try {
      const response = await api.post(`/trips/delete/${id}`, { reason });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message || "Failed to delete trip";
    }
  },

  resetTripStatusToCreated: async (tripId, reason) => {
    try {
      const response = await api.post(`/trips/reset-status-to-created`, { trip_id: tripId, reason });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message || "Failed to reset trip status";
    }
  },

  getTripCloserReport: async (id) => {
    try {
      const response = await api.get(`/trips/closer-report/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message || "Failed to fetch trip closure report";
    }
  },

  getFuelSheet: async (tripId) => {
    try {
      const response = await api.get(`/fuels/fuel-sheet/${tripId}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message || "Failed to generate fuel sheet";
    }
  },

  getBilty: async (tripId) => {
    try {
      const response = await api.get(`/trips/bilty/${tripId}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message || "Failed to generate bilty";
    }
  },

  getPod: async (tripId) => {
    try {
      const response = await api.get(`/trips/pod/${tripId}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message || "Failed to generate POD";
    }
  },

  tripSpeedChart: async (params) => {
    try {
      const response = await api.get(
        `/vehicles/speed-chart?trip_id=${params.trip_id}&date=${params.date}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  tripViolations: async (data, params) => {
    try {
      const url = `/trips/trip-violations?page=${params.page}&limit=${params.limit}`;
      const response = await api.post(url, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  saveGeofence: async (data) => {
    try {
      const response = await api.post(`/vehicle-geofences/create`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getAllGeofence: async () => {
    try {
      const response = await api.get(`/vehicle-geofences/all`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteGeofence: async (id) => {
    try {
      const response = await api.delete(`/vehicle-geofences/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ======================= PETTY CASH =========================

  getPettyCashSource: async () => {
    try {
      const response = await api.get(`/petty-cash/petty-cash-payment-source`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPettyCashModes: async () => {
    try {
      const response = await api.get(`/petty-cash/petty-cash-payment-modes`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createPettyCash: async (data) => {
    try {
      const response = await api.post(`/petty-cash/create`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPettyCashPaginated: async (params = {}) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 10;
      const url = `/petty-cash/paginated?page=${page}&limit=${limit}`;
      const body = {
        ...(params?.search_text ? { search_text: String(params.search_text).trim() } : {}),
        ...(params?.body && typeof params.body === "object" ? params.body : {}),
      };

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      // Assign Date is compared against a UTC-stored timestamp server-side, so
      // convert the selected local calendar-day range (plain "YYYY-MM-DD" from
      // the date filter) into its UTC-equivalent instant boundaries here, just
      // for this request - otherwise a record near local midnight gets counted
      // into the wrong day.
      const assignAtFilter = body.assignAtFilter;
      if (
        assignAtFilter?.filter_type === "date_range" &&
        assignAtFilter?.value?.startDate &&
        assignAtFilter?.value?.endDate
      ) {
        body.assignAtFilter = {
          ...assignAtFilter,
          value: {
            startDate: moment(`${assignAtFilter.value.startDate} 00:00:00`).utc().format("YYYY-MM-DD HH:mm:ss"),
            endDate: moment(`${assignAtFilter.value.endDate} 23:59:59`).utc().format("YYYY-MM-DD HH:mm:ss"),
          },
        };
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch petty cash entries"
      );
    }
  },

  getPettyCashListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/petty-cash/export-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate petty cash listing PDF"
      );
    }
  },

  getPettyCashListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/petty-cash/export-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate petty cash listing Excel"
      );
    }
  },

  updatePettyCashStatus: async (data) => {
    try {
      const response = await api.patch(`/petty-cash/change-status`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update petty cash status"
      );
    }
  },


  getInspectionItems: async (data) => {
    try {
      const response = await api.get(
        `/inspections/items?trip_id=${data.trip_id}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  saveInspectedItems: async (data) => {
    try {
      const response = await api.post(`/inspections/inspected-item`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ========================== TRIP EXPENSE ===========================

  getExpenseCategoryDropdown: async () => {
    try {
      const response = await api.get(
        `/trip-expenses/expenses-categories-dropdown`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createTripExpense: async (data) => {
    try {
      const response = await api.post(`/trip-expenses/create`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getTripExpensesPaginated: async (params = {}) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 10;
      const url = `/trip-expenses/paginated?page=${page}&limit=${limit}`;
      const body = {
        ...(params?.search_text ? { search_text: String(params.search_text).trim() } : {}),
      };

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch trip expenses"
      );
    }
  },

  getTripExpensesListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/trip-expenses/export-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate trip expenses listing PDF"
      );
    }
  },

  getTripExpensesListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/trip-expenses/export-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate trip expenses listing Excel"
      );
    }
  },

  // =========================== FUEL ========================

  createFuelLog: async (data) => {
    try {
      const response = await api.post(`/fuels/add-fuel-log`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createFuelClaim: async (data) => {
    try {
      const response = await api.post(`/fuels/fuel-claim`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit fuel claim"
      );
    }
  },

  getFuelStations: async () => {
    try {
      const response = await api.get(`/fuels/fuel-stations`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getCurrentFuelRate: async ({ fuel_type, fuel_station_id, changed_at }) => {
    try {
      const queryParams = new URLSearchParams({
        fuel_type: String(fuel_type || ""),
        fuel_station_id: String(fuel_station_id || ""),
      });

      if (String(changed_at || "").trim()) {
        queryParams.set("changed_at", String(changed_at).trim());
      }

      const response = await api.get(
        `/fuel-station/current-fuel-rate?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || error?.response?.data?.message;
    }
  },

  getTripListsDropdown: async () => {
    try {
      const response = await api.get(`/trips/trip-dropdown`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getClosedTripsDropdown: async () => {
    try {
      const response = await api.get(`/trips/closed-trips-dropdown`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch closed trips dropdown"
      );
    }
  },

  getAllTripsDropdown: async (search = "") => {
    try {
      const response = await api.get(`/trips/all-trips-dropdown`, {
        params: search ? { search } : {},
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trips dropdown"
      );
    }
  },

  getTripReportInfo: async (payload = {}) => {
    try {
      const response = await api.post(`/trips/trip-report-info`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip report info"
      );
    }
  },

  generateTripExpenseReport: async (payload = {}) => {
    try {
      const response = await api.post(`/financial-report/trip-expense-report`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate trip expense report"
      );
    }
  },

  generateTripPnlReport: async (payload = {}) => {
    try {
      const response = await api.post(`/financial-report/trip-pnl-report`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate trip PnL report"
      );
    }
  },

  generateVehiclePnlReport: async (payload = {}) => {
    try {
      const response = await api.post(`/financial-report/vehicle-pnl-report`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate vehicle PnL report"
      );
    }
  },

  generateMonthlyFleetPnlReport: async (payload = {}) => {
    try {
      const response = await api.post(`/financial-report/trip-pnl-monthly`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate monthly fleet PnL report"
      );
    }
  },


  exportCustomerPnlPdf: async (payload = {}) => {
    try {
      const response = await api.post(`/financial-report/customer-profit-and-loss/export-pdf`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to export Customer Profit & Loss PDF"
      );
    }
  },

  exportAccountsReceivablePdf: async (payload = {}) => {
    try {
      const response = await api.post(`/financial-report/account-receivable-pdf`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to export Accounts Receivable PDF"
      );
    }
  },

  getTripExpenseReportDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/financial-report/expense-detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip expense report detail"
      );
    }
  },

  getTripPnlReportDetail: async (id) => {
    const safeId = encodeURIComponent(String(id));

    try {
      const response = await api.get(`/financial-report/trip-pnl-detail/${safeId}`);
      return response.data;
    } catch (error) {
      if (Number(error?.response?.status || 0) === 404) {
        try {
          const fallbackResponse = await api.get(`/financial-report/expense-detail/${safeId}`);
          return fallbackResponse.data;
        } catch (fallbackError) {
          throw (
            fallbackError?.response?.data?.message?.[0] ||
            fallbackError?.response?.data?.message ||
            "Failed to fetch trip PnL report detail"
          );
        }
      }

      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip PnL report detail"
      );
    }
  },

  getVehiclePnlReportDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/financial-report/vehicle-pnl-detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle PnL report detail"
      );
    }
  },

  getMonthlyFleetPnlReportDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/financial-report/monthly-pnl-detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch monthly fleet PnL report detail"
      );
    }
  },

  getFinancialPnlReportDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/financial-report/pnl-customer-detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch PnL report details"
      );
    }
  },

  getFinancialArReportDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/financial-report/receivable-detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch AR report details"
      );
    }
  },

  getFinancialReportsPaginated: async (params = {}) => {
    const page = Number(params?.page) > 0 ? Number(params.page) : 1;
    const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
    const url = `/financial-report/paginated?page=${page}&limit=${limit}`;
    const { page: _p, limit: _l, ...body } = params;

    try {
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch financial reports"
      );
    }
  },

  getFinancialPnlReportsPaginated: async (params = {}) => {
    const page = Number(params?.page) > 0 ? Number(params.page) : 1;
    const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
    const url = `/financial-report/pnl-paginated?page=${page}&limit=${limit}`;
    const { page: _p, limit: _l, ...body } = params;

    try {
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch PnL financial reports"
      );
    }
  },

  getFinancialArReportsPaginated: async (params = {}) => {
    const page = Number(params?.page) > 0 ? Number(params.page) : 1;
    const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
    const url = `/financial-report/receivable-paginated?page=${page}&limit=${limit}`;
    const { page: _p, limit: _l, ...body } = params;

    try {
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch AR financial reports"
      );
    }
  },

  getDraftTripDropdown: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();

      if (params?.customer_id !== undefined && params?.customer_id !== null && params?.customer_id !== "") {
        searchParams.append("customer_id", String(params.customer_id));
      }

      if (params?.contract_id !== undefined && params?.contract_id !== null && params?.contract_id !== "") {
        searchParams.append("contract_id", String(params.contract_id));
      }

      const queryString = searchParams.toString();
      const response = await api.get(`/trips/draft-trip${queryString ? `?${queryString}` : ""}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch draft trips"
      );
    }
  },

  getTripInvoiceInfo: async (payload = {}) => {
    try {
      const response = await api.post("/trips-invoices/invoice-info", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch invoice info"
      );
    }
  },

  getTripsInvoicesPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) > 0 ? Number(params.page) : 1;
      const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(
        `/trips-invoices/paginated?page=${page}&limit=${limit}`,
        body
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch invoices"
      );
    }
  },

  getTripsInvoicesListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/trips-invoices/export-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate invoices listing PDF"
      );
    }
  },

  getTripsInvoicesListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/trips-invoices/export-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate invoices listing Excel"
      );
    }
  },

  getTripInvoiceDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/trips-invoices/detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch invoice detail"
      );
    }
  },

  exportTripInvoicePdf: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.post(`/trips-invoices/export-pdf/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to export invoice PDF"
      );
    }
  },

  getTripInvoiceHistory: async (params = {}) => {
    try {
      const safeInvoiceId = encodeURIComponent(String(params?.trips_invoice_id || ""));
      const safeHistoryType = encodeURIComponent(String(params?.history_type || "all"));
      const response = await api.get(
        `/trips-invoices/history?trips_invoice_id=${safeInvoiceId}&history_type=${safeHistoryType}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch invoice history"
      );
    }
  },

  commentOnTripInvoice: async (payload = {}) => {
    try {
      const response = await api.post("/trips-invoices/comment", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to comment on invoice"
      );
    }
  },

  getTripInvoicePayments: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/trips-invoices/payments/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch invoice payments"
      );
    }
  },

  createInvoicePayment: async (payload = {}) => {
    try {
      const response = await api.post("/invoices-payment/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create invoice payment"
      );
    }
  },

  updateInvoicePayment: async (id, payload = {}) => {
    try {
      const response = await api.post(`/invoices-payment/update/${id}`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update invoice payment"
      );
    }
  },

  deleteInvoicePayment: async (id) => {
    try {
      const response = await api.delete(`/invoices-payment/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete invoice payment"
      );
    }
  },

  getDriverPaymentsPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) > 0 ? Number(params.page) : 1;
      const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(
        `/driver-payment/paginated?page=${page}&limit=${limit}`,
        body
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver payments"
      );
    }
  },

  getDriverPayrollPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) > 0 ? Number(params.page) : 1;
      const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(
        `/driver-payroll/paginated?page=${page}&limit=${limit}`,
        body
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver payroll"
      );
    }
  },

  getActiveDriversPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) > 0 ? Number(params.page) : 1;
      const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
      const body = {
        search_text: params?.search_text ? String(params.search_text).trim() : "",
      };

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(
        `/driver/active-paginated?page=${page}&limit=${limit}`,
        body
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch active drivers"
      );
    }
  },

  createDriverPayroll: async (payload) => {
    try {
      const response = await api.post("/driver-payroll/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create driver payroll"
      );
    }
  },

  deleteDriverPayroll: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/driver-payroll/delete/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete driver payroll"
      );
    }
  },

  getPayrollDriversPaginated: async (payload = {}) => {
    try {
      const response = await api.post(
        `/driver-payroll/payroll-driver-paginated`,
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch payroll drivers"
      );
    }
  },

  upsertPayrollItems: async (payload) => {
    try {
      const response = await api.post(
        `/driver-payroll/upsert-items`,
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to upsert payroll items"
      );
    }
  },

  getDriverPayrollDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/driver-payroll/detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver payroll details"
      );
    }
  },

  approveRejectDriverPayroll: async (payload = {}) => {
    try {
      const response = await api.post(`/driver-payroll/approve-reject`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update payroll approval status"
      );
    }
  },

  getDriverPaymentDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/driver-payment/detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver payment detail"
      );
    }
  },

  getDriverExpenseApprovalsPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) > 0 ? Number(params.page) : 1;
      const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
      const response = await api.post(`/expenses/paginated?page=${page}&limit=${limit}`, {});
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver expense approvals"
      );
    }
  },

  getExpenseLedgerPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) > 0 ? Number(params.page) : 1;
      const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
      const response = await api.post(
        `/expenses/ledger-paginated?page=${page}&limit=${limit}`,
        {}
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch expense ledger"
      );
    }
  },

  getDriverExpenseDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/expenses/trip-expense/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch expense detail"
      );
    }
  },

  getDriverPaymentHistory: async (params = {}) => {
    try {
      const driverInfoId =
        params?.driver_info_id ?? params?.driverId ?? params?.id ?? params?.source_id;
      const historyType = params?.history_type || "all";
      const safeDriverInfoId = encodeURIComponent(String(driverInfoId || ""));
      const safeHistoryType = encodeURIComponent(String(historyType));
      const response = await api.get(
        `/driver/history?driver_info_id=${safeDriverInfoId}&history_type=${safeHistoryType}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver payment history"
      );
    }
  },

  commentOnDriverPayment: async (payload = {}) => {
    try {
      const response = await api.post("/driver-payment/comment", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to comment on driver payment"
      );
    }
  },

  addDriverPaymentAllowance: async (payload = {}) => {
    try {
      const response = await api.post("/driver-payment/add-allowances", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to add driver payment allowance"
      );
    }
  },

  updateDriverPaymentAllowance: async (payload = {}) => {
    try {
      const response = await api.patch("/driver-payment/update-allowance", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update driver payment allowance"
      );
    }
  },

  deleteDriverPaymentAllowance: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/driver-payment/delete-allowance/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete driver payment allowance"
      );
    }
  },

  addDriverPaymentDeduction: async (payload = {}) => {
    try {
      const response = await api.post("/driver-payment/add-deductions", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to add driver payment deduction"
      );
    }
  },

  updateDriverPaymentDeduction: async (payload = {}) => {
    try {
      const response = await api.patch("/driver-payment/update-deduction", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update driver payment deduction"
      );
    }
  },

  deleteDriverPaymentDeduction: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/driver-payment/delete-deduction/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete driver payment deduction"
      );
    }
  },

  createTripInvoice: async (payload = {}) => {
    try {
      const response = await api.post("/trips-invoices/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create trip invoice"
      );
    }
  },

  getEligibleTrips: async (payload = {}) => {
    try {
      const response = await api.post("/trips-invoices/eligible-trips", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch eligible trips"
      );
    }
  },

  getCalculationPreview: async (payload = {}) => {
    try {
      const response = await api.post("/trips-invoices/calculation-preview", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch calculation preview"
      );
    }
  },

  getTripEstimateDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/trips/trip-estimate-detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip estimate detail"
      );
    }
  },

  getTripSettlementDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/trips/settlement-detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip settlement detail"
      );
    }
  },

  getTripCostEstimatePaginated: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const url = `/trip-cost-estimate/paginated?page=${page}&limit=${limit}`;
      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip cost estimations"
      );
    }
  },

  getContractsPaginated: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const url = `/contracts/paginated?page=${page}&limit=${limit}`;
      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch contracts"
      );
    }
  },

  getContractsListingPdf: async (data = {}) => {
    try {
      const response = await api.post("/contracts/export-pdf", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate contracts listing PDF"
      );
    }
  },

  getContractsListingExcel: async (data = {}) => {
    try {
      const response = await api.post("/contracts/export-excel", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate contracts listing Excel"
      );
    }
  },

  getContractDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/contracts/detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch contract detail"
      );
    }
  },

  addContractVehicleType: async (payload) => {
    try {
      const response = await api.post("/contracts/add-vehicle-type", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to add vehicle type to contract"
      );
    }
  },

  updateContractVehicleSetting: async (payload) => {
    try {
      const response = await api.patch("/contracts/update-vehicle-setting", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update vehicle settings"
      );
    }
  },

  saveContractVehicles: async (payload) => {
    try {
      const response = await api.post("/contracts/save-contract-vehicles", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to save contract vehicles"
      );
    }
  },

  getContractVehicles: async (contractId, vehicleTypeId) => {
    try {
      const safeId = encodeURIComponent(String(contractId));
      const params = vehicleTypeId ? { vehicle_type_id: vehicleTypeId } : {};
      const response = await api.get(`/contracts/contract-vehicles/${safeId}`, { params });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch contract vehicles"
      );
    }
  },



  getTripCostEstimateDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/trip-cost-estimate/detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip cost estimation detail"
      );
    }
  },

  getTripCostEstimateHistory: async (params) => {
    try {
      const tripCostEstimateId =
        params?.trip_cost_estimate_id ??
        params?.tripCostEstimateId ??
        params?.id ??
        params?.source_id;
      const historyType = params?.history_type || "all";
      const safeTripCostEstimateId = encodeURIComponent(String(tripCostEstimateId));
      const safeHistoryType = encodeURIComponent(String(historyType));
      const response = await api.get(
        `/trip-cost-estimate/history?trip_cost_estimate_id=${safeTripCostEstimateId}&history_type=${safeHistoryType}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip cost estimation history"
      );
    }
  },

  createTripCostEstimate: async (payload) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && payload instanceof FormData;
      const response = await api.post(`/trip-cost-estimate/add`, payload, {
        headers: isFormData
          ? {
            "Content-Type": "multipart/form-data",
          }
          : undefined,
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create trip cost estimation"
      );
    }
  },

  createContract: async (payload) => {
    try {
      const response = await api.post("/contracts/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create contract"
      );
    }
  },

  updateContract: async (id, payload) => {
    try {
      const response = await api.patch("/contracts/update", {
        contract_id: Number(id),
        ...payload,
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update contract"
      );
    }
  },

  deleteContract: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/contracts/delete/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete contract"
      );
    }
  },

  commentOnTripCostEstimate: async (payload) => {
    try {
      const response = await api.post(`/trip-cost-estimate/comment`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to comment on trip cost estimation"
      );
    }
  },

  updateTripCostEstimate: async (payload) => {
    try {
      const response = await api.patch(`/trip-cost-estimate/update`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update trip cost estimation"
      );
    }
  },

  deleteTripCostEstimate: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/trip-cost-estimate/delete/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete trip cost estimation"
      );
    }
  },

  overviewFuel: async () => {
    try {
      const response = await api.get(`/fuels/overview`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVehiclesOverview: async () => {
    try {
      const response = await api.get("/vehicles/overview");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicles overview"
      );
    }
  },

  getTyresOverview: async () => {
    try {
      const response = await api.get("/tyres/overview");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyres overview"
      );
    }
  },

  getTyresListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/tyres/export-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate tyres listing PDF"
      );
    }
  },

  getTyresListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/tyres/export-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate tyres listing Excel"
      );
    }
  },

  getVehiclesPaginated: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const url = `/vehicles/all-vehicles-paginated?page=${page}&limit=${limit}`;
      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      try {
        const response = await api.post(url, body);
        return response.data;
      } catch (error) {
        const status = error?.response?.status;
        if (status !== 404 && status !== 405) {
          throw error;
        }

        const response = await api.get(url);
        return response.data;
      }
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicles"
      );
    }
  },

  getVehicleListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/vehicles/export-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate vehicle listing PDF"
      );
    }
  },

  getVehiclesListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/vehicles/all-vehicles-export-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate vehicles listing PDF"
      );
    }
  },

  getVehicleListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/vehicles/export-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate vehicle listing Excel"
      );
    }
  },

  getVehiclesListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/vehicles/all-vehicles-export-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate vehicles listing Excel"
      );
    }
  },

  fuelLogsLists: async (params) => {
    try {
      const url = `/fuels/fuel-trips?page=${params.page}&limit=${params.limit}`;
      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getFuelLogsPaginated: async (params = {}, payload = {}) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const response = await api.post(
        `/fuels/fuel-logs-paginated?page=${page}&limit=${limit}`,
        payload || {}
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch fuel logs"
      );
    }
  },

  getFuelLogsListingPdf: async (payload = {}) => {
    try {
      const response = await api.post("/fuels/fuel-logs-listing-pdf", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate fuel logs PDF"
      );
    }
  },

  getFuelLogsListingExcel: async (payload = {}) => {
    try {
      const response = await api.post("/fuels/fuel-logs-listing-excel", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate fuel logs Excel"
      );
    }
  },

  fuelDetails: async (id) => {
    try {
      const response = await api.get(`/fuels/fuel-trip-detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  fuelGeneralDetails: async (id) => {
    try {
      const response = await api.get(`/fuels/fuel-trip-general-detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteFuelLogs: async (id) => {
    try {
      const response = await api.delete(`/fuel-station/delete-fuel-log/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        try {
          const fallbackResponse = await api.delete(`/fuels/delete-fuel-log/${id}`);
          return fallbackResponse.data;
        } catch (fallbackError) {
          throw (
            fallbackError?.response?.data?.message?.[0] ||
            fallbackError?.response?.data?.message ||
            error?.response?.data?.message?.[0] ||
            error?.response?.data?.message ||
            "Failed to delete fuel log"
          );
        }
      }
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete fuel log"
      );
    }
  },

  deleteGalleryFile: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/gallery/delete/${safeId}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  uploadedFuelFiles: async (data) => {
    try {
      const response = await api.post(`/fuels/fuel-files`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateFuelFinanceStatus: async (data) => {
    try {
      const response = await api.post(`/trips/update-finance-status`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ================== VEHICLES ===================

  getVehiclePaginated: async (params) => {
    try {
      const url = `/vehicles/vehicles-paginated`;
      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVehicleDetails: async (data) => {
    try {
      const response = await api.get(
        `/vehicles/detail?vehicle_id=${data.vehicle_id}&vehicle_brand_id=${data.vehicle_brand_id}&ref_id=${data.ref_id}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVehicleDetailsByFuelLog: async (vehicleId) => {
    try {
      const response = await api.get(
        `/vehicles/detail-by-fuel?vehicle_id=${vehicleId}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVehicleSingleDetail: async (vehicleId) => {
    try {
      const safeVehicleId = encodeURIComponent(String(vehicleId));
      const response = await api.get(`/vehicles/single/${safeVehicleId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle detail"
      );
    }
  },

  getVehicleServices: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const response = await api.post(`/vehicles/services?page=${page}&limit=${limit}`, {
        vehicle_id: Number(params?.vehicle_id),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle service records"
      );
    }
  },

  getVehicleTrips: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const response = await api.post(`/vehicles/trips?page=${page}&limit=${limit}`, {
        vehicle_id: Number(params?.vehicle_id),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle trips"
      );
    }
  },

  getVehicleFuelLogs: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const response = await api.post(`/vehicles/fuels-logs?page=${page}&limit=${limit}`, {
        vehicle_id: Number(params?.vehicle_id),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle fuel logs"
      );
    }
  },

  getVehicleDistanceSummary: async (vehicleId) => {
    try {
      const safeVehicleId = encodeURIComponent(String(vehicleId));
      const response = await api.get(`/vehicles/distance-summary?vehicle_id=${safeVehicleId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle distance summary"
      );
    }
  },

  getVehicleInstallments: async (vehicleId) => {
    try {
      const safeVehicleId = encodeURIComponent(String(vehicleId));
      const response = await api.get(`/vehicles/vehicle-installments-list?vehicle_id=${safeVehicleId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle installments"
      );
    }
  },

  createVehicleInstallment: async (payload) => {
    try {
      const response = await api.post("/vehicles/vehicle-installments", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create vehicle installment"
      );
    }
  },

  attachVehicleEquipment: async (payload) => {
    try {
      const response = await api.post("/vehicles/attached-equipment", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to link vehicle equipment"
      );
    }
  },

  detachVehicleEquipment: async (payload) => {
    try {
      const response = await api.post("/vehicles/detach-equipment", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to unlink vehicle equipment"
      );
    }
  },

  attachVehicleTyres: async (payload) => {
    try {
      const response = await api.post("/tyres/attached-tyre", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to link tyres"
      );
    }
  },

  detachTyre: async (payload) => {
    try {
      const response = await api.post("/tyres/detach-tyre", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to detach tyre"
      );
    }
  },

  getVehicleAssignmentHistory: async (vehicleId) => {
    try {
      const safeVehicleId = encodeURIComponent(String(vehicleId));
      const response = await api.get(`/vehicles/assignment-history?vehicle_id=${safeVehicleId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle assignment history"
      );
    }
  },

  updateVehicleGeneralInfo: async (payload) => {
    try {
      const response = await api.patch("/vehicles/update-general-info", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update vehicle general information"
      );
    }
  },

  updateVehiclePurchaseInfo: async (payload) => {
    try {
      const response = await api.patch("/vehicles/update-purchase-info", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update vehicle purchase information"
      );
    }
  },

  createHiredVehicle: async (payload) => {
    try {
      const response = await api.post("/vehicles/create-hired", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create hired vehicle"
      );
    }
  },

  createPurchasedVehicle: async (payload) => {
    try {
      const response = await api.post("/vehicles/create-purchase", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create purchased vehicle"
      );
    }
  },

  addThreePlVehicle: async (payload) => {
    try {
      const response = await api.post("/vehicles/add-3pl-vehicle", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to add 3PL vehicle"
      );
    }
  },

  updateThreePlVehicle: async (payload) => {
    try {
      const response = await api.patch("/vehicles/update-3pl-vehicle", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update 3PL vehicle"
      );
    }
  },

  deleteVehicle: async (id) => {
    try {
      const response = await api.delete(`/vehicles/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getRefuelingHistoryByVehicle: async (params) => {
    try {
      const url = `/fuels/fuel-history-by-vehicle?page=${params.page}&limit=${params.limit}`;
      const body = { vehicle_id: params.vehicle_id };
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVehicleHistory: async (params) => {
    try {
      const url = `/vehicles/vehicle-history?history_type=${params.history_type}&vehicle_id=${params.vehicle_id}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  commentOnVehicle: async (data) => {
    try {
      const response = await api.post("/vehicles/comment-on-vehicle", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getAppModules: async () => {
    try {
      const response = await api.get("/app-business-module/find-all");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  changeAppModuleOrder: async (data) => {
    try {
      const response = await api.patch(
        "/app-business-module/change-order",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateAppModule: async (data) => {
    try {
      const response = await api.patch("/app-business-module/update", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createAppModule: async (data) => {
    try {
      const response = await api.post("/app-business-module/create", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createSubModule: async (data) => {
    try {
      const response = await api.post(
        "/app-business-module/create-sub-module",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateSubModule: async (data) => {
    try {
      const response = await api.patch(
        "/app-business-module/update-sub-module",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  changeSubModuleOrder: async (data) => {
    try {
      const response = await api.patch(
        "/app-business-module/change-sub-module-order",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  changePermissionOrder: async (data) => {
    try {
      const response = await api.patch(
        "/app-business-module/change-permission-order",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createPermission: async (data) => {
    try {
      const response = await api.post(
        "/app-business-module/create-permission",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updatePermission: async (data) => {
    try {
      const response = await api.patch(
        "/app-business-module/update-permission",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteModule: async (id) => {
    try {
      const response = await api.delete(
        `app-business-module/delete-module/${id}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteSubModule: async (id) => {
    try {
      const response = await api.delete(
        `app-business-module/delete-sub-module/${id}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteBusinessPermission: async (id) => {
    try {
      const response = await api.delete(
        `app-business-module/delete-permission/${id}`,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ======================= PARTS =========================

  getInventoryPartsList: async () => {
    try {
      const response = await api.get("/parts/parts-list");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartsPaginated: async (params) => {
    try {
      const url = `/parts/paginated?page=${params.page}&limit=${params.limit}`;

      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch parts"
      );
    }
  },

  getPartsListingPdf: async (data = {}) => {
    try {
      const response = await api.post("/parts/export-pdf", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate parts listing PDF"
      );
    }
  },

  getPartsListingExcel: async (data = {}) => {
    try {
      const response = await api.post("/parts/export-excel", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate parts listing Excel"
      );
    }
  },

  getPartsOverview: async () => {
    try {
      const response = await api.get(`/parts/overview`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] || "Failed to fetch parts overview"
      );
    }
  },

  deleteParts: async (id) => {
    try {
      const response = await api.delete(`parts/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartDetail: async (id) => {
    try {
      const response = await api.get(`parts/detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartPaginatedForPO: async (params) => {
    try {
      const url = `/parts/part-paginated-for-po?page=${params.page}&limit=${params.limit}`;
      const body = { part_id: Number(params.part_id) };
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to fetch part procurement information"
      );
    }
  },

  getPartUtilization: async (params) => {
    try {
      const url = `/parts/part-utilization?page=${params.page}&limit=${params.limit}`;
      const body = { part_id: Number(params.part_id) };
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to fetch part utilization information"
      );
    }
  },

  getPartId: async () => {
    try {
      const response = await api.get(`parts/generate-id`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartCategoriesDropdown: async () => {
    try {
      const response = await api.get(`/app-dropdown/part-categories`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartsMaterialDropdown: async () => {
    try {
      const response = await api.get(`/app-dropdown/part-materials`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartsModelDropdown: async () => {
    try {
      const response = await api.get(`/app-dropdown/part-models`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartsMakesDropdown: async () => {
    try {
      const response = await api.get(`/app-dropdown/part-makes`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartsUnitsDropdown: async () => {
    try {
      const response = await api.get(`/app-dropdown/part-units`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getTransporterCitiesDropdown: async (headquarter = false) => {
    try {
      const query = headquarter ? "?headquarter=true" : "";
      const response = await api.get(`/app-dropdown/cities${query}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch cities dropdown"
      );
    }
  },

  getTransporterVehicleTypesDropdown: async () => {
    try {
      const response = await api.get(`/app-dropdown/vehicle-types`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle types dropdown"
      );
    }
  },

  getVehicleTypesDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/vehicle-types");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle types dropdown"
      );
    }
  },

  getVehicleVendorsDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/vehicle/vendors");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle vendors dropdown"
      );
    }
  },

  getVehicleThirdPartyOwnersDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/vehicle/third-party-owners");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle third-party owners dropdown"
      );
    }
  },

  getVehicleConstructionTypesDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/vehicle/construction_types");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle construction types dropdown"
      );
    }
  },

  getVehicleTransmissionTypesDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/vehicle/transmission-types");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle transmission types dropdown"
      );
    }
  },

  getVehicleMakesDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/vehicle/makes");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle makes dropdown"
      );
    }
  },

  getVehicleModelsDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/vehicle/models");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle models dropdown"
      );
    }
  },

  getTyreBrandsDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/tyres/brands");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre brands dropdown"
      );
    }
  },

  getTyreModelsDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/tyres/models");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre models dropdown"
      );
    }
  },

  getTyrePatternsDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/tyres/patterns");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre patterns dropdown"
      );
    }
  },

  getTyreTypesDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/tyres/types");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre types dropdown"
      );
    }
  },

  getTyresDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/tyres-dropdown");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyres dropdown"
      );
    }
  },

  getCompanyBranchesDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/company-branches");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch company branches dropdown"
      );
    }
  },

  getEquipmentTypesDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/equipment/equipment-types");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment types dropdown"
      );
    }
  },

  getEquipmentManufacturersDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/equipment/equipment-manufacturers");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment manufacturers dropdown"
      );
    }
  },

  getEquipmentModelsDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/equipment/equipment-models");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment models dropdown"
      );
    }
  },

  getEquipmentVendorsDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/equipment/equipment-vendors");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment vendors dropdown"
      );
    }
  },

  getEquipmentDropdown: async () => {
    try {
      const response = await api.get("/app-dropdown/equipment-dropdown");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment dropdown"
      );
    }
  },

  getEquipmentData: async (params) => {
    try {
      const url = `/equipment/paginated?page=${params.page}&limit=${params.limit}`;
      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      try {
        const response = await api.post(url, body);
        return response.data;
      } catch (error) {
        const status = error?.response?.status;
        if (status !== 404 && status !== 405) {
          throw error;
        }

        const response = await api.get(url);
        return response.data;
      }
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipments"
      );
    }
  },

  getEquipmentListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/equipment/export-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate equipment listing PDF"
      );
    }
  },

  getEquipmentListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/equipment/export-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate equipment listing Excel"
      );
    }
  },

  getEquipmentOverview: async () => {
    try {
      const response = await api.get("/equipment/overview");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment overview"
      );
    }
  },

  getEquipmentDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/equipment/detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment detail"
      );
    }
  },

  getEquipmentServiceDetail: async (equipmentId, serviceId) => {
    try {
      const safeEquipmentId = encodeURIComponent(String(equipmentId));
      const safeServiceId = encodeURIComponent(String(serviceId));
      const response = await api.get(
        `/equipment/service-detail?equipment_id=${safeEquipmentId}&service_id=${safeServiceId}`,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment service detail"
      );
    }
  },

  getEquipmentServices: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const response = await api.post(`/equipment/services?page=${page}&limit=${limit}`, {
        equipment_id: Number(params?.equipment_id),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment service records"
      );
    }
  },

  getEquipmentInstallments: async (equipmentId) => {
    try {
      const safeEquipmentId = encodeURIComponent(String(equipmentId));
      const response = await api.get(
        `/equipment/equipment-installments-list?equipment_id=${safeEquipmentId}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment installments"
      );
    }
  },

  getEquipmentDistanceSummary: async (equipmentId) => {
    try {
      const safeEquipmentId = encodeURIComponent(String(equipmentId));
      const response = await api.get(
        `/equipment/distance-summary?equipment_id=${safeEquipmentId}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment distance summary"
      );
    }
  },

  getEquipmentTrips: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const response = await api.post(`/equipment/trips?page=${page}&limit=${limit}`, {
        equipment_id: Number(params?.equipment_id),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment trips"
      );
    }
  },

  createEquipmentInstallment: async (payload) => {
    try {
      const response = await api.post("/equipment/equipment-installments", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create equipment installment"
      );
    }
  },

  createTyre: async (payload) => {
    try {
      const response = await api.post("/tyres/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create tyre"
      );
    }
  },

  updateTyreGeneralInfo: async (payload) => {
    try {
      const response = await api.patch("/tyres/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update tyre details"
      );
    }
  },

  getAssetTyres: async (params) => {
    try {
      const assetId = Number(params?.asset_id);
      const assetType = String(params?.asset_type || "").trim().toLowerCase();
      const response = await api.get(
        `/tyres/asset-tyres?asset_id=${assetId}&asset_type=${assetType}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch asset tyres"
      );
    }
  },

  swipeTyres: async (payload) => {
    try {
      const response = await api.post("/tyres/swipe", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to swipe tyres"
      );
    }
  },

  createEquipment: async (payload) => {
    try {
      const response = await api.post("/equipment/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create equipment"
      );
    }
  },

  updateEquipmentGeneralInfo: async (payload) => {
    try {
      const response = await api.patch("/equipment/update-general-info", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update equipment details"
      );
    }
  },

  updateEquipmentPurchaseInfo: async (payload) => {
    try {
      const response = await api.patch("/equipment/update-purchase-info", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update equipment purchase information"
      );
    }
  },

  deleteEquipment: async (id) => {
    try {
      const response = await api.delete(`/equipment/delete/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete equipment"
      );
    }
  },

  getTyreDetail: async (id) => {
    try {
      const response = await api.get(`/tyres/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre detail"
      );
    }
  },

  getTyreServiceDetail: async (tyreId, serviceId) => {
    try {
      const safeTyreId = encodeURIComponent(String(tyreId));
      const safeServiceId = encodeURIComponent(String(serviceId));
      const response = await api.get(
        `/tyres/service-detail?tyre_id=${safeTyreId}&service_id=${safeServiceId}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre service detail"
      );
    }
  },

  getTyreHistory: async (params) => {
    try {
      const url = `/tyres/tyre-history?history_type=${params.history_type}&tyre_id=${params.tyre_id}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre history"
      );
    }
  },

  getTyreServices: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const response = await api.post(`/tyres/services?page=${page}&limit=${limit}`, {
        tyre_id: Number(params?.tyre_id),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre service records"
      );
    }
  },

  getTyreTrips: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const response = await api.post(`/tyres/trips?page=${page}&limit=${limit}`, {
        tyre_id: Number(params?.tyre_id),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre trips"
      );
    }
  },

  getTyreAssignmentHistory: async (params) => {
    try {
      const response = await api.post("/tyres/history", {
        tyre_id: Number(params?.tyre_id),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tyre assignment history"
      );
    }
  },

  commentOnTyre: async (data) => {
    try {
      const response = await api.post("/tyres/tyre-comment", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to comment on tyre"
      );
    }
  },

  getEquipmentHistory: async (params) => {
    try {
      const historyId =
        params?.equipment_id ?? params?.tyre_id ?? params?.id ?? params?.source_id;
      const historyType = params?.history_type || "all";
      const safeHistoryId = encodeURIComponent(String(historyId));
      const safeHistoryType = encodeURIComponent(String(historyType));
      const response = await api.get(
        `/equipment/history?equipment_id=${safeHistoryId}&history_type=${safeHistoryType}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment history"
      );
    }
  },

  getEquipmentAssignmentHistory: async (equipmentId) => {
    try {
      const safeEquipmentId = encodeURIComponent(String(equipmentId));
      const response = await api.get(
        `/equipment/assignment-history?equipment_id=${safeEquipmentId}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch equipment assignment history"
      );
    }
  },

  commentOnEquipment: async (data) => {
    try {
      const response = await api.post("/equipment/comment", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to comment on equipment"
      );
    }
  },

  deleteTyre: async (id) => {
    try {
      const response = await api.delete(`/tyres/delete/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete tyre"
      );
    }
  },

  createPart: async (data) => {
    try {
      const response = await api.post(`/parts/create`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createVendor: async (data) => {
    try {
      const response = await api.post(`/part-vendors/create`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createWarehouse: async (data) => {
    try {
      const response = await api.post(`/warehouses/create`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createDriver: async (data) => {
    try {
      const response = await api.post(`/driver/add`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create Driver"
      );
    }
  },

  getDriversPaginated: async (params) => {
    try {
      const url = `/driver/paginated?page=${params.page}&limit=${params.limit}`;
      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch Drivers"
      );
    }
  },

  getDriverListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/driver/export-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate driver listing PDF"
      );
    }
  },

  getDriverListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/driver/export-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate driver listing Excel"
      );
    }
  },

  getDriverOverview: async () => {
    try {
      const response = await api.get("/driver/overview");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch Driver overview"
      );
    }
  },

  getDriverDetail: async (id) => {
    try {
      const response = await api.get(`/driver/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch Driver detail"
      );
    }
  },

  getDriverHistory: async (params) => {
    try {
      const driverInfoId =
        params?.driver_info_id ?? params?.driverId ?? params?.id ?? params?.source_id;
      const historyType = params?.history_type || "all";
      const safeDriverInfoId = encodeURIComponent(String(driverInfoId));
      const safeHistoryType = encodeURIComponent(String(historyType));
      const response = await api.get(
        `/driver/history?driver_info_id=${safeDriverInfoId}&history_type=${safeHistoryType}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch Driver history"
      );
    }
  },

  generateDriverPassword: async () => {
    try {
      const response = await api.get("/auth/generate-driver-password");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate Driver password"
      );
    }
  },

  generateDriverIdCard: async (payload) => {
    try {
      const response = await api.post("/driver/id-card", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate Driver ID card"
      );
    }
  },

  getDriverExpensesPaginated: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 8;
      const url = `/driver/paginated-expenses?page=${page}&limit=${limit}`;
      const body = {
        driver_id: Number(params?.driver_id),
      };

      if (params?.search_text) {
        body.search_text = String(params.search_text).trim();
      }

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch Driver expenses"
      );
    }
  },

  getDriverPayroll: async (params = {}) => {
    try {
      const response = await api.post("/driver/driver-payroll", {
        drive_id: Number(params?.drive_id || params?.driver_id || 0),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch Driver payroll"
      );
    }
  },

  getDriverAttendance: async (params = {}) => {
    try {
      const response = await api.post("/driver/driver-attendance", {
        drive_id: Number(params?.drive_id || params?.driver_id || 0),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch Driver attendance"
      );
    }
  },

  getDriverLeaves: async (params = {}) => {
    try {
      const response = await api.post("/driver/driver-leaves", {
        drive_id: Number(params?.drive_id || params?.driver_id || 0),
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch Driver leaves"
      );
    }
  },

  updateDriver: async (payload) => {
    try {
      const response = await api.patch("/driver/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update Driver"
      );
    }
  },

  deleteDriver: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/driver/delete/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete Driver"
      );
    }
  },

  commentOnDriver: async (data) => {
    try {
      const response = await api.post("/driver/comment", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to comment on Driver"
      );
    }
  },

  updateDriverStatus: async (payload) => {
    try {
      const response = await api.patch("/driver/status", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update Driver status"
      );
    }
  },

  getVendorsPaginated: async (params) => {
    try {
      const url = `/part-vendors/paginated?page=${params.page}&limit=${params.limit}`;

      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getRFQByVendor: async (params) => {
    try {
      const response = await api.get("/parts-rfq/rfq-by-vendor", { params });
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message || "Failed to fetch RFQ";
    }
  },

  submitVendorBid: async (data) => {
    try {
      const response = await api.post("/parts-rfq-bids/bid-by-vendor", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || "Failed to submit bid";
    }
  },

  getWarehouseOverview: async () => {
    try {
      const response = await api.get("/warehouses/overview");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message || "Failed to fetch Warehouse Overview"
      );
    }
  },

  getRFQOverview: async () => {
    try {
      const response = await api.get("/parts-rfq/overview");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message || "Failed to fetch RFQ overview";
    }
  },

  getRFQDetail: async (id) => {
    try {
      const response = await api.get(`/parts-rfq/detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message || "Failed to fetch RFQ details";
    }
  },

  updatePartRFQ: async (data) => {
    try {
      const response = await api.patch("/parts-rfq/update", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message || "Failed to update RFQ";
    }
  },

  createPartRFQ: async (data) => {
    try {
      const response = await api.post("/parts-rfq/create", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updatePartRFQStatus: async (data) => {
    try {
      const response = await api.patch("/parts-rfq/status", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  resendRfq: async (part_rfq_id) => {
    try {
      const response = await api.post("/parts-rfq/resend-rfq", { part_rfq_id });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to resend RFQ"
      );
    }
  },

  deletePartRFQ: async (id) => {
    try {
      const response = await api.delete(`/parts-rfq/delete/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete RFQ"
      );
    }
  },

  updateSupplyChainStatus: async (data) => {
    try {
      const response = await api.patch("/parts-rfq/supply-chain-status", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update supply chain status"
      );
    }
  },

  createPurchaseOrder: async (data) => {
    try {
      const response = await api.post("/parts-purchase-orders/create", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPurchaseOrdersPaginated: async (params) => {
    try {
      const url = `/parts-purchase-orders/paginated?page=${params.page}&limit=${params.limit}`;
      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartsPurchaseOrdersListingPdf: async (data = {}) => {
    try {
      const response = await api.post('/parts-purchase-orders/export-pdf', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export purchase orders listing PDF'
      );
    }
  },

  getPartsPurchaseOrdersListingExcel: async (data = {}) => {
    try {
      const response = await api.post('/parts-purchase-orders/export-excel', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export purchase orders listing Excel'
      );
    }
  },

  getPurchaseOrdersOverview: async () => {
    try {
      const response = await api.get("/parts-purchase-orders/overview");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to fetch Purchase Orders overview"
      );
    }
  },

  getPurchaseOrderDetail: async (id) => {
    try {
      const response = await api.get(`/parts-purchase-orders/detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || "Failed to fetch PO details";
    }
  },

  getPurchaseOrderFinanceDetail: async (id) => {
    try {
      const response = await api.get(
        `/parts-purchase-orders/finance-detail/${id}`,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to fetch PO finance details"
      );
    }
  },

  getPurchaseOrderExportPdf: async (id) => {
    try {
      const response = await api.get(
        `/parts-purchase-orders/export-pdf/${id}`,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to export purchase order PDF"
      );
    }
  },

  updatePurchaseOrderGeneralDetail: async (data) => {
    try {
      const response = await api.patch(
        "/parts-purchase-orders/update-general-detail",
        data,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to update PO general details"
      );
    }
  },

  updatePurchaseOrderVerification: async (data) => {
    try {
      const response = await api.patch(
        "/parts-purchase-orders/po-verification",
        data,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to update PO verification details"
      );
    }
  },

  updatePurchaseOrderPaymentTransactions: async (data) => {
    try {
      const hasTransactionId =
        Number.isFinite(Number(data?.transaction_id)) &&
        Number(data.transaction_id) > 0;
      const response = hasTransactionId
        ? await api.patch(
          "/parts-purchase-orders/update-po-payment-transactions",
          data,
        )
        : await api.post("/parts-purchase-orders/po-payment-transactions", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to update PO payment transactions"
      );
    }
  },

  deletePurchaseOrderPaymentTransaction: async (transactionId) => {
    try {
      const response = await api.delete(
        `/parts-purchase-orders/delete-po-payment-transactions/${transactionId}`,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to delete PO payment transaction"
      );
    }
  },

  deletePurchaseOrder: async (id) => {
    try {
      const response = await api.delete(`/parts-purchase-orders/delete/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete purchase order"
      );
    }
  },

  getRFQBidsPaginated: async (params) => {
    try {
      const url = `/parts-rfq-bids/paginated?page=${params.page}&limit=${params.limit}`;
      const body = { parts_rfq_id: params.parts_rfq_id };
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getRFQsPaginated: async (params) => {
    try {
      const url = `/parts-rfq/paginated?page=${params.page}&limit=${params.limit}`;
      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartsRfqListingPdf: async (data = {}) => {
    try {
      const response = await api.post('/parts-rfq/export-pdf', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export RFQ listing PDF'
      );
    }
  },

  getPartsRfqListingExcel: async (data = {}) => {
    try {
      const response = await api.post('/parts-rfq/export-excel', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export RFQ listing Excel'
      );
    }
  },

  getRFQHistory: async (params) => {
    try {
      const url = `/parts-rfq/part-rfq-history?history_type=${params.history_type}&parts_rfq_id=${params.parts_rfq_id}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  commentOnRFQ: async (data) => {
    try {
      const response = await api.post("/parts-rfq/part-rfq-comment", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getWarehousesPaginated: async (params) => {
    try {
      const url = `/warehouses/paginated?page=${params.page}&limit=${params.limit}`;

      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteVendor: async (id) => {
    try {
      const response = await api.delete(`/part-vendors/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

    getWarehousesListingPdf: async (data = {}) => {
    try {
      const response = await api.post('/warehouses/export-pdf', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export Warehouses listing PDF'
      );
    }
  },

  getWarehousesListingExcel: async (data = {}) => {
    try {
      const response = await api.post('/warehouses/export-excel', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export Warehouses listing Excel'
      );
    }
  },

    getPartVendorsDropdown: async () => {
    try {
      const response = await api.get('/part-vendors/dropdown');
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message?.[0] || 'Failed to fetch vendors dropdown';
    }
  },

  getPartVendorsListingPdf: async (data = {}) => {
    try {
      const response = await api.post('/part-vendors/export-pdf', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export Vendors listing PDF'
      );
    }
  },

  getPartVendorsListingExcel: async (data = {}) => {
    try {
      const response = await api.post('/part-vendors/export-excel', data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export Vendors listing Excel'
      );
    }
  },

deleteWarehouse: async (id) => {
    try {
      const response = await api.delete(`/warehouses/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getWarehouseDetail: async (id) => {
    try {
      const response = await api.get(`/warehouses/detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getWarehousePartsPaginated: async (params) => {
    try {
      const url = `/parts-stock/warehouse-paginated?page=${params.page}&limit=${params.limit}`;
      const body = { warehouse_id: params.warehouse_id };
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to fetch warehouse inventory"
      );
    }
  },

  getStockMovementInfo: async (params) => {
    try {
      const url = `/parts-stock/stock-movement-info?page=${params.page}&limit=${params.limit}`;
      const body = { warehouse_id: params.warehouse_id };
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to fetch stock movement info"
      );
    }
  },

  deleteStockMovementInfo: async (id) => {
    try {
      const response = await api.delete(
        `/parts-stock/stock-movement-info-delete/${id}`,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] || "Failed to delete stock movement"
      );
    }
  },

  changeStockMovementStatus: async (data) => {
    try {
      const response = await api.post(
        `/parts-stock/stock-movement-info-change-status`,
        data,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to change stock movement status"
      );
    }
  },

  getWarehousesDropdown: async () => {
    try {
      const response = await api.get("/warehouses/dropdown");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to fetch warehouses dropdown"
      );
    }
  },

  getWarehousesWithPartStock: async (partId) => {
    try {
      const response = await api.get(
        `/warehouses/warehouses-with-part-stock/${partId}`,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        "Failed to fetch warehouses with part stock"
      );
    }
  },

  getPartsByWarehouse: async (warehouseId) => {
    try {
      const response = await api.get(
        `/parts-stock/part-list?warehouse_id=${warehouseId}`,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] || "Failed to fetch warehouse parts"
      );
    }
  },

  createWarehouseStockTransfer: async (data) => {
    try {
      const response = await api.post(
        "/parts-stock/warehouse-stock-transfer",
        data,
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] || "Failed to create stock transfer"
      );
    }
  },

  updateWarehouse: async (data) => {
    try {
      const response = await api.patch("/warehouses/update", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVendorDetail: async (id) => {
    try {
      const response = await api.get(`/part-vendors/detail/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updatePartVendor: async (data) => {
    try {
      const response = await api.patch("/part-vendors/update", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVendorOverview: async () => {
    try {
      const response = await api.get("/part-vendors/overview");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartHistory: async (params) => {
    try {
      const url = `/parts/part-history?history_type=${params.history_type}&part_id=${params.part_id}`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  commentOnParts: async (data) => {
    try {
      const response = await api.post("/parts/part-comment", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updatePart: async ({ data }) => {
    try {
      const response = await api.patch(`/parts/update`, data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ============== FUEL STATION ===================

  createFuelStation: async (data) => {
    try {
      const response = await api.post("/fuel-station/add-fuel-station", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getFuelCompaniesDropdown: async () => {
    try {
      const response = await api.get("fuels/fuel-companies");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getFuelStationPaginated: async (params) => {
    try {
      const url = `/fuel-station/paginated?page=${params.page}&limit=${params.limit}`;

      const body = params.search_text
        ? { search_text: params.search_text }
        : {};
      if (params.searchFilters) {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getFuelStationListingPdf: async (data = {}) => {
    try {
      const response = await api.post("/fuel-station/export-pdf", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate fuel station listing PDF"
      );
    }
  },

  getFuelStationListingExcel: async (data = {}) => {
    try {
      const response = await api.post("/fuel-station/export-excel", data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate fuel station listing Excel"
      );
    }
  },

  deleteFuelStation: async (id) => {
    try {
      const response = await api.delete(`/fuel-station/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getFuelStationDetails: async (id) => {
    try {
      const response = await api.get(`/fuel-station/details/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateFuelStation: async (data) => {
    try {
      const response = await api.patch(
        "/fuel-station/update-fuel-station",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateFuelRate: async (data) => {
    try {
      const response = await api.patch("/fuel-station/update-fuel-rate", data);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },
  getFuelStationHistory: async (params) => {
    try {
      const url = `/fuel-station/fuel-station-history?fuel_station_id=${params.fuel_station_id}&history_type=${params.history_type}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },
  commentOnFuelStation: async (data) => {
    try {
      const response = await api.post(
        "/fuel-station/fuel-station-comment",
        data,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getLoadBookingOverview: async () => {
    try {
      const response = await api.get("/three-pl-load/overview");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch load booking overview"
      );
    }
  },

  createLoadBooking: async (payload) => {
    try {
      const response = await api.post("/three-pl-load/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create load booking"
      );
    }
  },

  getLoadBookingsPaginated: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const url = `/three-pl-load/paginated?page=${page}&limit=${limit}`;

      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch load bookings"
      );
    }
  },

  getLoadBookingDetail: async (id) => {
    try {
      const response = await api.get(`/three-pl-load/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch load booking detail"
      );
    }
  },

  getTransporterOverview: async () => {
    try {
      const response = await api.get("/three-pl-transporter/overview");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch transporter overview"
      );
    }
  },

  getTransportersPaginated: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const url = `/three-pl-transporter/paginated?page=${page}&limit=${limit}`;

      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch transporters"
      );
    }
  },

  getCustomersPaginated: async (params) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const url = `/three-pl-customers/paginated?page=${page}&limit=${limit}`;

      const body = params?.search_text
        ? { search_text: String(params.search_text).trim() }
        : {};

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch customers"
      );
    }
  },

  getCustomerOverview: async () => {
    try {
      const response = await api.get("/three-pl-customers/overview");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch customer overview"
      );
    }
  },

  getCustomerListingPdf: async (payload = {}) => {
    try {
      const response = await api.post(
        "/trip-customer/customer-listing-pdf",
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate customer listing PDF"
      );
    }
  },

  getCustomerListingExcel: async (payload = {}) => {
    try {
      const response = await api.post(
        "/trip-customer/customer-listing-excel",
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate customer listing Excel"
      );
    }
  },

  getCustomerDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/three-pl-customers/detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch customer detail"
      );
    }
  },

  exportCustomerLocationsPdf: async (customerId, payload = {}) => {
    try {
      const safeId = encodeURIComponent(String(customerId));
      const response = await api.post(
        `/three-pl-customers/locations/export-pdf/${safeId}`,
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate customer locations PDF"
      );
    }
  },

  exportCustomerLocationsExcel: async (customerId, payload = {}) => {
    try {
      const safeId = encodeURIComponent(String(customerId));
      const response = await api.post(
        `/three-pl-customers/locations/export-excel/${safeId}`,
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate customer locations Excel"
      );
    }
  },

  exportCustomerProductsPdf: async (customerId, payload = {}) => {
    try {
      const safeId = encodeURIComponent(String(customerId));
      const response = await api.post(
        `/three-pl-customers/products/export-pdf/${safeId}`,
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate customer products PDF"
      );
    }
  },

  exportCustomerProductsExcel: async (customerId, payload = {}) => {
    try {
      const safeId = encodeURIComponent(String(customerId));
      const response = await api.post(
        `/three-pl-customers/products/export-excel/${safeId}`,
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate customer products Excel"
      );
    }
  },

  getCustomerTripDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/three-pl-customers/trip-detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch customer trip detail"
      );
    }
  },

  getCustomerRoutesList: async (customerId) => {
    try {
      const safeCustomerId = encodeURIComponent(String(customerId));
      const response = await api.get(
        `/customer-routes/list?customer_id=${safeCustomerId}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch customer routes"
      );
    }
  },

  getCustomerRouteRateLogsPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const url = `/customer-routes/paginated-rate-logs/?page=${page}&limit=${limit}`;
      const body = {};

      if (params?.customer_id !== undefined && params?.customer_id !== null) {
        body.customer_id = params.customer_id;
      }

      if (
        params?.customer_route_id !== undefined &&
        params?.customer_route_id !== null
      ) {
        body.customer_route_id = params.customer_route_id;
      }

      if (String(params?.search_text || "").trim()) {
        body.search_text = String(params.search_text).trim();
      }

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch customer route rate logs"
      );
    }
  },

  getCustomerRouteLocationDropdown: async (customerId) => {
    try {
      const safeCustomerId = encodeURIComponent(String(customerId));
      const response = await api.get(
        `/customer-routes/location-dropdown/${safeCustomerId}`
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch customer route locations"
      );
    }
  },

  createCustomerRoute: async (payload) => {
    try {
      const response = await api.post("/customer-routes/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create customer route"
      );
    }
  },

  updateCustomerRoute: async (payload) => {
    try {
      const response = await api.patch("/customer-routes/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update customer route"
      );
    }
  },

  updateCustomerRouteLocationRate: async (payload) => {
    try {
      const response = await api.patch(
        "/customer-routes/update-location-rate",
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update customer route location rate"
      );
    }
  },

  getProductRatesPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const url = `/products/product-rates-paginated/?page=${page}&limit=${limit}`;
      const body = {};

      if (params?.product_id !== undefined && params?.product_id !== null) {
        body.product_id = params.product_id;
      }

      if (String(params?.search_text || "").trim()) {
        body.search_text = String(params.search_text).trim();
      }

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch product rate logs"
      );
    }
  },
  exportProductRatesPdf: async (payload = {}) => {
    try {
      const response = await api.post('/products/product-rates-export-pdf', payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export Product Rates PDF'
      );
    }
  },

  exportProductRatesExcel: async (payload = {}) => {
    try {
      const response = await api.post('/products/product-rates-export-excel', payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export Product Rates Excel'
      );
    }
  },
  exportFuelStationRatesPdf: async (payload = {}) => {
    try {
      const response = await api.post(
        '/fuel-station/fuel-rates-export-pdf',
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export Fuel Station Rates PDF'
      );
    }
  },

  exportFuelStationRatesExcel: async (payload = {}) => {
    try {
      const response = await api.post(
        '/fuel-station/fuel-rates-export-excel',
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        'Failed to export Fuel Station Rates Excel'
      );
    }
  },

  updateProductRate: async (payload) => {
    try {
      const response = await api.patch(
        "/products/update-product-rate",
        payload
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update product rate"
      );
    }
  },

  getContractTripDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/contracts/contract-trip-details/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch contract trip detail"
      );
    }
  },

  createCustomer: async (payload) => {
    try {
      const response = await api.post("/three-pl-customers/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create customer"
      );
    }
  },

  updateCustomer: async (payload) => {
    try {
      const response = await api.patch("/three-pl-customers/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update customer"
      );
    }
  },

  deleteCustomer: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/three-pl-customers/delete/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete customer"
      );
    }
  },

  createProduct: async (payload) => {
    try {
      const response = await api.post("/products/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create product"
      );
    }
  },

  updateProduct: async (payload) => {
    try {
      const response = await api.patch("/products/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update product"
      );
    }
  },

  deleteProduct: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/products/delete/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete product"
      );
    }
  },

  createCustomerWarehouse: async (payload) => {
    try {
      const response = await api.post("/customer-warehouses/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create warehouse location"
      );
    }
  },

  updateCustomerWarehouse: async (payload) => {
    try {
      const response = await api.patch("/customer-warehouses/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update warehouse location"
      );
    }
  },

  deleteCustomerWarehouse: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/customer-warehouses/delete/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete warehouse location"
      );
    }
  },

  getThreePlTransporterDropdown: async () => {
    try {
      const response = await api.get("/three-pl-transporter/dropdown");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch transporter dropdown"
      );
    }
  },

  addThreePlTransporter: async (payload) => {
    try {
      const response = await api.post("/three-pl-transporter/add", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to add transporter"
      );
    }
  },

  createTransporter: async (payload) => {
    try {
      const response = await api.post("/three-pl-transporter/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create transporter"
      );
    }
  },

  getTransporterDetail: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.get(`/three-pl-transporter/detail/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch transporter detail"
      );
    }
  },

  updateTransporter: async (payload) => {
    try {
      const response = await api.patch("/three-pl-transporter/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update transporter"
      );
    }
  },

  deleteTransporter: async (id) => {
    try {
      const safeId = encodeURIComponent(String(id));
      const response = await api.delete(`/three-pl-transporter/delete/${safeId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete transporter"
      );
    }
  },

  getMaintenanceCategories: async (params) => {
    try {
      const response = await api.post(
        `/maintenance-categories/paginated?page=${params.page}&limit=${params.limit}`,
        params.body,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getCategoriesOverview: async () => {
    try {
      const response = await api.get("/maintenance-categories/overview");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createCategory: async (payload) => {
    try {
      const response = await api.post(
        "/maintenance-categories/create",
        payload,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateCategory: async (payload) => {
    try {
      const response = await api.patch(
        "/maintenance-categories/update",
        payload,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteCategory: async (id) => {
    try {
      const response = await api.delete(`/maintenance-categories/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ============== VEHICLE TYPES ===================

  getVehicleTypesPaginated: async (params) => {
    try {
      const response = await api.post(
        `/vehicle-types/paginated?page=${params.page}&limit=${params.limit}`,
        params.body,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getVehicleTypesOverview: async () => {
    try {
      const response = await api.get("/vehicle-types/overview");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createVehicleType: async (payload) => {
    try {
      const response = await api.post("/vehicle-types/create", payload);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updateVehicleType: async (payload) => {
    try {
      const response = await api.patch("/vehicle-types/update", payload);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deleteVehicleType: async (id) => {
    try {
      const response = await api.delete(`/vehicle-types/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ============== PART MODELS ===================

  getPartModelsPaginated: async (params) => {
    try {
      const response = await api.post(
        `/part-models/paginated?page=${params.page}&limit=${params.limit}`,
        params.body,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartModelsOverview: async () => {
    try {
      const response = await api.get("/part-models/overview");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createPartModel: async (payload) => {
    try {
      const response = await api.post("/part-models/create", payload);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updatePartModel: async (payload) => {
    try {
      const response = await api.patch("/part-models/update", payload);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deletePartModel: async (id) => {
    try {
      const response = await api.delete(`/part-models/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ============== PART MAKES ===================

  getPartMakesPaginated: async (params) => {
    try {
      const response = await api.post(
        `/part-makes/paginated?page=${params.page}&limit=${params.limit}`,
        params.body,
      );
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  getPartMakesOverview: async () => {
    try {
      const response = await api.get("/part-makes/overview");
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  createPartMake: async (payload) => {
    try {
      const response = await api.post("/part-makes/create", payload);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  updatePartMake: async (payload) => {
    try {
      const response = await api.patch("/part-makes/update", payload);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  deletePartMake: async (id) => {
    try {
      const response = await api.delete(`/part-makes/delete/${id}`);
      return response.data;
    } catch (error) {
      throw error?.response?.data?.message[0];
    }
  },

  // ============== TAX RULES ==============
  getTaxRulesPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) > 0 ? Number(params.page) : 1;
      const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
      const response = await api.post(
        `/tax-rule/paginated?page=${page}&limit=${limit}`,
        {}
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tax rules"
      );
    }
  },

  createTaxRule: async (payload = {}) => {
    try {
      const response = await api.post("/tax-rule/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create tax rule"
      );
    }
  },

  getTaxRuleDetail: async (id) => {
    try {
      const response = await api.get(`/tax-rule/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch tax rule details"
      );
    }
  },

  updateTaxRule: async (payload = {}) => {
    try {
      const response = await api.patch("/tax-rule/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update tax rule"
      );
    }
  },

  deleteTaxRule: async (id) => {
    try {
      const response = await api.delete(`/tax-rule/delete/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete tax rule"
      );
    }
  },

  // ============== FINANCE WORKFLOWS ==============
  getWorkflowsPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) > 0 ? Number(params.page) : 1;
      const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
      const response = await api.post(
        `/finance-workflow/paginated?page=${page}&limit=${limit}`,
        {
          search_text: params?.search_text || "",
          searchFilters: params?.searchFilters || {},
        }
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch workflows"
      );
    }
  },

  createWorkflow: async (payload = {}) => {
    try {
      const response = await api.post("/finance-workflow/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create workflow"
      );
    }
  },

  getWorkflowDetail: async (id) => {
    try {
      const response = await api.get(`/finance-workflow/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch workflow details"
      );
    }
  },

  updateWorkflow: async (payload = {}) => {
    try {
      const response = await api.patch("/finance-workflow/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update workflow"
      );
    }
  },

  deleteWorkflow: async (id) => {
    try {
      const response = await api.delete(`/finance-workflow/delete/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete workflow"
      );
    }
  },

  // ============== FMCR ==============
  getFmcrVehicleStatus: async () => {
    try {
      const response = await api.get("/fmcr/fmcr-vehicle-status");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch fmcr vehicle status counts"
      );
    }
  },

  getFmcrVehicleDropdown: async (type = "all") => {
    try {
      const response = await api.get(`/fmcr/fmcr-vehicle-dropdown?type=${type}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch fmcr vehicles dropdown"
      );
    }
  },

  getFmcrTripDropdown: async () => {
    try {
      const response = await api.get("/fmcr/fmcr-trip-dropdown");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch fmcr trips dropdown"
      );
    }
  },

  getFmcrVehicles: async (params = {}) => {
    try {
      const url = "/fmcr/vehicles";
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;

      const rawStatus = String(params?.status || "all").trim();
      const body = {
        status: rawStatus.toLowerCase() === "all" ? "all" : rawStatus.toUpperCase(),
      };

      if (params?.search_text) {
        body.search_text = String(params.search_text).trim();
      }

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(`${url}?page=${page}&limit=${limit}`, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch fmcr vehicles"
      );
    }
  },

  getFmcrVehiclesView: async (payload = {}) => {
    try {
      const response = await api.post("/fmcr/vehicles-view", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch fmcr vehicles view"
      );
    }
  },

  getFmcrStatusesVehiclePaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) || 1;
      const limit = Number(params?.limit) || 10;
      const url = `/fmcr-statuses/vehicle-paginated?page=${page}&limit=${limit}`;

      // This endpoint only accepts POST (GET always 404s), so post directly.
      const body = {};
      if (params?.search_text) {
        body.search_text = String(params.search_text).trim();
      }
      // vehicleTypeFilter / ownershipTypeFilter / statusFilter, each {filter_type, value}
      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }
      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch fmcr vehicle statuses"
      );
    }
  },

  getFmcrVehicleTrackingCard: async (id) => {
    try {
      const response = await api.get(`/fmcr/vehicle-tracking-card/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle tracking card"
      );
    }
  },

  getFmcrVehicleDetail: async (id) => {
    try {
      const response = await api.get(`/fmcr/vehicle-detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle detail"
      );
    }
  },

  getFmcrVehicleHistory: async (params = {}) => {
    try {
      const response = await api.get("/fmcr/vehicle-history", { params });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle history"
      );
    }
  },

  getFmcrVehicleHistorySummary: async (payload = {}) => {
    try {
      const response = await api.post("/fmcr/vehicle-history-summary", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle history summary"
      );
    }
  },

  getFmcrDriversActivities: async (payload = {}, params = {}) => {
    try {
      const page = params.page || 1;
      const limit = params.limit || 10;
      const response = await api.post(`/fmcr/drivers-activities-paginated?page=${page}&limit=${limit}`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver activities"
      );
    }
  },

  getFmcrAlerts: async (payload = {}, params = {}) => {
    try {
      const page = params.page || 1;
      const limit = params.limit || 10;
      const response = await api.post(`/fmcr/alerts-paginated?page=${page}&limit=${limit}`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch paginated alerts"
      );
    }
  },

  getFmcrAttachments: async (payload = {}, params = {}) => {
    try {
      const page = params.page || 1;
      const limit = params.limit || 10;
      const response = await api.post(`/fmcr/attachments-paginated?page=${page}&limit=${limit}`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch attachments"
      );
    }
  },

  getFmcrReportsPaginated: async (params = {}) => {
    try {
      const page = params.page || 1;
      const limit = params.limit || 10;
      const url = `/fmcr-reports/paginated?page=${page}&limit=${limit}`;
      const payload = {};
      if (params.search) {
        payload.search_text = String(params.search).trim();
        payload.search = String(params.search).trim();
      }
      const response = await api.post(url, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch paginated fmcr reports"
      );
    }
  },

  getFmcrTripJourneySegments: async (tripId) => {
    try {
      const response = await api.get(`/fmcr/trip-journey-segments?id=${tripId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip journey segments"
      );
    }
  },

  exportFmcrTripJourneySegmentsPdf: async (tripId) => {
    try {
      const response = await api.get(`/fmcr/trip-journey-segments-export-pdf/${tripId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to export trip journey segments PDF"
      );
    }
  },

  getFmcrTripViolations: async (tripId, sync = false) => {
    try {
      const response = await api.get(`/fmcr/trip-violations?id=${tripId}${sync ? "&sync=true" : ""}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch trip violations"
      );
    }
  },

  getFmcrVehicleJourneySegments: async (params = {}) => {
    try {
      const pad = (n) => String(n).padStart(2, '0');
      const formatSql = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      const now = new Date();
      const defaultFrom = formatSql(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0));
      const defaultTo = formatSql(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59));

      const queryParams = {
        ...params,
        from: params.from ? (params.from.includes('T') ? params.from.replace('T', ' ') : params.from) : defaultFrom,
        to: params.to ? (params.to.includes('T') ? params.to.replace('T', ' ') : params.to) : defaultTo,
      };

      const response = await api.get("/fmcr/vehicle-journey-segments", { params: queryParams });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle journey segments"
      );
    }
  },

  exportFmcrVehicleJourneySegmentsPdf: async (params = {}) => {
    try {
      const pad = (n) => String(n).padStart(2, '0');
      const formatSql = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      const now = new Date();
      const defaultFrom = formatSql(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0));
      const defaultTo = formatSql(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59));

      const queryParams = {
        ...params,
        from: params.from ? (params.from.includes('T') ? params.from.replace('T', ' ') : params.from) : defaultFrom,
        to: params.to ? (params.to.includes('T') ? params.to.replace('T', ' ') : params.to) : defaultTo,
      };

      const response = await api.get("/fmcr/vehicle-journey-segments-export-pdf", { params: queryParams });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to export vehicle journey segments PDF"
      );
    }
  },

  exportFmcrVehicleHistoryPdf: async (params = {}) => {
    try {
      const pad = (n) => String(n).padStart(2, '0');
      const formatSql = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      const now = new Date();
      const defaultFrom = formatSql(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0));
      const defaultTo = formatSql(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59));

      const queryParams = {
        ...params,
        from: params.from ? (params.from.includes('T') ? params.from.replace('T', ' ') : params.from) : defaultFrom,
        to: params.to ? (params.to.includes('T') ? params.to.replace('T', ' ') : params.to) : defaultTo,
      };

      const response = await api.get("/fmcr/vehicle-history-export-pdf", { params: queryParams });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to export vehicle history PDF"
      );
    }
  },

  exportFmcrVehiclesLiveSnapshotPdf: async (payload = {}) => {
    try {
      const response = await api.post("/fmcr/vehicles-live-snapshot-export-pdf", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to export live snapshot PDF"
      );
    }
  },

  exportFmcrVehicleHistorySummaryPdf: async (payload = {}) => {
    try {
      const pad = (n) => String(n).padStart(2, '0');
      const formatSql = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      const now = new Date();
      const defaultFrom = formatSql(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0));
      const defaultTo = formatSql(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59));

      const body = {
        ...payload,
        from: payload.from ? (payload.from.includes('T') ? payload.from.replace('T', ' ') : payload.from) : defaultFrom,
        to: payload.to ? (payload.to.includes('T') ? payload.to.replace('T', ' ') : payload.to) : defaultTo,
      };

      const response = await api.post("/fmcr/vehicle-history-summary-export-pdf", body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to export fleet history summary PDF"
      );
    }
  },

  createFmcrManualLog: async (payload = {}) => {
    try {
      const response = await api.post("/fmcr-statuses/create-manual-log", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create FMCR manual log"
      );
    }
  },

  createFmcrReport: async (payload = {}) => {
    try {
      const response = await api.post("/fmcr-reports/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create FMCR report"
      );
    }
  },

  updateFmcrReport: async (payload = {}) => {
    try {
      const response = await api.patch("/fmcr-reports/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update FMCR report"
      );
    }
  },

  deleteFmcrReport: async (id) => {
    try {
      const response = await api.delete(`/fmcr-reports/delete/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete FMCR report"
      );
    }
  },

  getFmcrReportDetail: async (id) => {
    try {
      const response = await api.get(`/fmcr-reports/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch FMCR report detail"
      );
    }
  },

  getFmcrVehiclesStatusTracking: async (payload = {}, params = {}) => {
    try {
      const page = params.page || 1;
      const limit = params.limit || 10;
      const response = await api.post(`/fmcr/vehicles-status-tracking?page=${page}&limit=${limit}`, payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicles status tracking"
      );
    }
  },

  getFmcrVehicleLogsByImeino: async (imeino) => {
    try {
      const response = await api.get(`/fmcr/vehicle-logs?imeino=${encodeURIComponent(imeino)}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch vehicle logs"
      );
    }
  },

  // ============== INVOICE COLUMNS ==============
  getInvoiceColumnMaster: async () => {
    try {
      const response = await api.get("/invoice-column-configuration/column-master");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch invoice column master"
      );
    }
  },

  getInvoiceColumnConfigsPaginated: async (params = {}) => {
    try {
      const page = Number(params?.page) > 0 ? Number(params.page) : 1;
      const limit = Number(params?.limit) > 0 ? Number(params.limit) : 10;
      const response = await api.post(
        `/invoice-column-configuration/paginated?page=${page}&limit=${limit}`,
        {
          search_text: params?.search_text || "",
          searchFilters: params?.searchFilters || {},
        }
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch invoice column configurations"
      );
    }
  },

  createInvoiceColumnConfig: async (payload = {}) => {
    try {
      const response = await api.post("/invoice-column-configuration/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create invoice column configuration"
      );
    }
  },

  getInvoiceColumnConfigDetail: async (id) => {
    try {
      const response = await api.get(`/invoice-column-configuration/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch invoice column configuration details"
      );
    }
  },

  updateInvoiceColumnConfig: async (payload = {}) => {
    try {
      const response = await api.patch("/invoice-column-configuration/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update invoice column configuration"
      );
    }
  },

  deleteInvoiceColumnConfig: async (id) => {
    try {
      const response = await api.delete(`/invoice-column-configuration/delete/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete invoice column configuration"
      );
    }
  },

  // ============== ROLES ==============
  getRolesDropdown: async () => {
    try {
      const response = await api.get("/app-roles/dropdown");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch roles dropdown"
      );
    }
  },

  getRolePermissionsTree: async (roleId) => {
    try {
      const response = await api.get(`/app-roles/role-permissions-tree/${roleId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch role permissions tree"
      );
    }
  },

  assignRolePermissions: async (payload = {}) => {
    try {
      const response = await api.post("/app-roles/assign-permissions", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to assign permissions"
      );
    }
  },

  getRoles: async (page = 1, limit = 10, search = "") => {
    try {
      const response = await api.post(
        `/app-roles/paginated?page=${page}&limit=${limit}`,
        search ? { search } : {}
      );
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch roles"
      );
    }
  },

  createRole: async (payload = {}) => {
    try {
      const response = await api.post("/app-roles/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create role"
      );
    }
  },

  getRoleDetail: async (id) => {
    try {
      const response = await api.get(`/app-roles/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch role details"
      );
    }
  },

  updateRole: async (payload = {}) => {
    try {
      const response = await api.patch("/app-roles/update", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update role"
      );
    }
  },

  deleteRole: async (id) => {
    try {
      const response = await api.delete(`/app-roles/delete/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete role"
      );
    }
  },

  getAllUsersDropdown: async () => {
    try {
      const response = await api.get("/auth/all-users-dropdown");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch users dropdown"
      );
    }
  },

  assignRole: async (payload = {}) => {
    try {
      const response = await api.post("/app-roles/assign-role", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to assign role"
      );
    }
  },

  getAllAttendance: async (date = "") => {
    try {
      const response = await api.get("/attendance/get-all-attendance", {
        params: date ? { date } : {}
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver attendance list"
      );
    }
  },

  saveDriverAttendance: async (payload = {}) => {
    try {
      const response = await api.post("/attendance/mark-driver-attendance", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to save driver attendance"
      );
    }
  },

  unmarkDriverAttendance: async (payload = {}) => {
    try {
      const response = await api.post("/attendance/unmark-driver-attendance", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to unmark driver attendance"
      );
    }
  },

  getDriverAttendanceDetails: async (params = {}) => {
    try {
      const response = await api.get("/attendance/get-driver-attendance", {
        params
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver attendance details"
      );
    }
  },

  getPaginatedAttendance: async (payload = {}, params = {}) => {
    try {
      const response = await api.post("/attendance/paginated", payload, {
        params
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch attendance summary"
      );
    }
  },

  getDriverAttendanceLogs: async (payload = {}, params = {}) => {
    try {
      const response = await api.post("/attendance/driver-logs", payload, {
        params
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver attendance logs"
      );
    }
  },

  getAttendanceListingPdf: async (data = {}) => {
    try {
      const response = await api.post(`/attendance/export-pdf`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate attendance listing PDF"
      );
    }
  },

  getAttendanceListingExcel: async (data = {}) => {
    try {
      const response = await api.post(`/attendance/export-excel`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to generate attendance listing Excel"
      );
    }
  },

  // ─── Driver Allowances ────────────────────────────────────────────────────

  saveDriverAllowances: async (driverInfoId, allowances = []) => {
    try {
      const response = await api.post("/attendance/driver-allowances", {
        driver_info_id: driverInfoId,
        allowances,
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to save driver allowances"
      );
    }
  },

  getDriverAllowances: async (driverInfoId) => {
    try {
      const response = await api.get(`/attendance/driver-allowances/${driverInfoId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver allowances"
      );
    }
  },

  deleteDriverAllowance: async (id) => {
    try {
      const response = await api.delete(`/attendance/driver-allowances/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete driver allowance"
      );
    }
  },

  saveAllowanceEntries: async (entries = []) => {
    try {
      const response = await api.post("/attendance/allowance-entries", { entries });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to save allowance entries"
      );
    }
  },

  getAllowanceEntries: async (driverInfoIds = [], date = "") => {
    try {
      const response = await api.get("/attendance/allowance-entries", {
        params: { driver_info_ids: driverInfoIds.join(","), date },
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch allowance entries"
      );
    }
  },

  getDriverActiveAllowancesForDate: async (driverInfoId, date) => {
    try {
      const response = await api.get("/attendance/driver-active-allowances", {
        params: { driver_info_id: driverInfoId, date },
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch driver allowances for date"
      );
    }
  },

  // ─── Payroll Attendance Summary ───────────────────────────────────────────

  getPayrollAttendanceSummary: async (driverInfoIds = [], startDate, endDate) => {
    try {
      const response = await api.get("/attendance/payroll-summary", {
        params: {
          driver_info_ids: driverInfoIds.join(","),
          start_date: startDate,
          end_date: endDate,
        },
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch payroll attendance summary"
      );
    }
  },

  // ─── Shortage Installments ────────────────────────────────────────────────

  createShortageInstallment: async (payload) => {
    try {
      const response = await api.post("/attendance/shortage-installments", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create shortage installment"
      );
    }
  },

  getShortageInstallments: async (driverInfoId) => {
    try {
      const response = await api.get(`/attendance/shortage-installments/${driverInfoId}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch shortage installments"
      );
    }
  },

  deleteShortageInstallment: async (id) => {
    try {
      const response = await api.delete(`/attendance/shortage-installments/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete shortage installment"
      );
    }
  },

  getPendingShortageInstallments: async (driverInfoIds = []) => {
    try {
      const response = await api.get("/attendance/pending-shortage-installments", {
        params: { driver_info_ids: driverInfoIds.join(",") },
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch pending shortage installments"
      );
    }
  },

  getFinanceDashboardHome: async () => {
    try {
      const response = await api.get("/finance-dashboard/home");
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch finance dashboard data"
      );
    }
  },

  approveRejectTripInvoice: async (payload = {}) => {
    try {
      const response = await api.post("/trips-invoices/approve-reject", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update invoice status"
      );
    }
  },

  getContentPaginated: async (payload = {}, params = {}) => {
    try {
      const page = Number(params?.page || payload?.page) || 1;
      const limit = Number(params?.limit || payload?.limit) || 10;
      const requestBody = {
        ...payload,
      };

      const response = await api.post("/content/paginated", requestBody, {
        params: {
          page,
          limit,
          ...params,
        },
      });
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch content"
      );
    }
  },

  createContent: async (payload) => {
    try {
      const response = await api.post("/content/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to create content"
      );
    }
  },

  getContentDetail: async (id) => {
    try {
      const response = await api.get(`/content/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to fetch content details"
      );
    }
  },

  updateContent: async (payload) => {
    try {
      const response = await api.patch("/content/update", payload);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 405) {
        const responsePost = await api.post("/content/update", payload);
        return responsePost.data;
      }
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to update content"
      );
    }
  },

  deleteContent: async (id) => {
    try {
      const response = await api.delete(`/content/delete/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 405) {
        const responsePost = await api.post(`/content/delete/${id}`);
        return responsePost.data;
      }
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Failed to delete content"
      );
    }
  },

  createFuelRequest: async (payload = {}) => {
    try {
      const response = await api.post("/fuel-requests/create", payload);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit fuel request"
      );
    }
  },

  getFuelRequestsPaginated: async (params = {}) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 10;
      const url = `/fuel-requests/paginated?page=${page}&limit=${limit}`;
      const body = {
        ...(params?.search_text ? { search_text: String(params.search_text).trim() } : {}),
        ...(params?.body && typeof params.body === "object" ? params.body : {}),
      };

      if (params?.searchFilters && typeof params.searchFilters === "object") {
        Object.assign(body, params.searchFilters);
      }

      const response = await api.post(url, body);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch fuel requests"
      );
    }
  },

  deleteFuelRequest: async (id) => {
    try {
      const response = await api.delete(`/fuel-requests/delete/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 405) {
        try {
          const postRes = await api.post(`/fuel-requests/delete/${id}`);
          return postRes.data;
        } catch (postErr) {
          throw (
            postErr?.response?.data?.message?.[0] ||
            postErr?.response?.data?.message ||
            postErr?.message ||
            "Failed to delete fuel request"
          );
        }
      }
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete fuel request"
      );
    }
  },

  getFuelRequestDetail: async (id) => {
    try {
      const response = await api.get(`/fuel-requests/detail/${id}`);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch fuel request details"
      );
    }
  },

  updateFuelRequest: async (payload = {}) => {
    try {
      const response = await api.patch("/fuel-requests/update", payload);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 405) {
        try {
          const postRes = await api.post("/fuel-requests/update", payload);
          return postRes.data;
        } catch (postErr) {
          throw (
            postErr?.response?.data?.message?.[0] ||
            postErr?.response?.data?.message ||
            postErr?.message ||
            "Failed to update fuel request"
          );
        }
      }
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update fuel request"
      );
    }
  },

  updateFuelRequestStatus: async (data) => {
    try {
      const response = await api.patch(`/fuel-requests/change-status`, data);
      return response.data;
    } catch (error) {
      throw (
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update fuel request status"
      );
    }
  },
};
